package org.gymly.ui.admin

import org.gymly.util.GymlyCurrency

import androidx.compose.foundation.clickable
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Add
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material.icons.outlined.Image
import androidx.compose.material.icons.outlined.MoreVert
import androidx.compose.material.icons.outlined.Payments
import androidx.compose.material.icons.outlined.Print
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material.icons.outlined.Remove
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import coil3.compose.AsyncImage
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.data.PickedMediaHolder
import org.gymly.model.AdminClient
import org.gymly.model.AdminPendingApprovalException
import org.gymly.model.AdminProduct
import org.gymly.model.AdminStoreSale
import org.gymly.model.AdminStorePaymentLine
import org.gymly.model.AdminStoreSaleItemUpsert
import androidx.compose.ui.text.style.TextOverflow
import org.gymly.model.AdminStoreSaleMethod
import org.gymly.model.AdminStoreSaleOrigin
import org.gymly.model.AdminStoreSaleStatus
import org.gymly.model.AdminStoreSaleUpsert
import org.gymly.model.AdminStoreWalkInCustomer
import org.gymly.model.AdminWorker
import org.gymly.model.PickedImagePayload
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlySpace
import org.gymly.ui.components.AdminDateField
import org.gymly.ui.components.AdminDateFieldFilterWidth
import org.gymly.ui.components.ProofAttachmentView
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.util.AdminDateOutputFormat
import org.gymly.util.downloadBytes
import org.gymly.util.formatMoney
import org.gymly.util.formatIsoToDisplayDate
import org.gymly.util.parseFlexibleDateToIso
import org.gymly.util.desdeSieteDiasIso
import org.gymly.util.todayIsoDate
import org.gymly.util.userMessage

/** Máximo de ventas visibles por página en el listado. */
private const val STORE_SALES_PAGE_SIZE = 20

/** Acción de validar comprobante de venta; null si la licencia o el permiso no la permiten. */
private val LocalStoreSaleValidation = staticCompositionLocalOf<((AdminStoreSale) -> Unit)?> { null }

private sealed class StoreSalesDialog {
    data object Create : StoreSalesDialog()
    data class Detail(val sale: AdminStoreSale) : StoreSalesDialog()
    /** Cobrar pendiente: elegir método y registrar pago → Pagada. */
    data class Collect(val sale: AdminStoreSale) : StoreSalesDialog()
    data class Delete(val sale: AdminStoreSale) : StoreSalesDialog()
    /** Validar comprobante "Por validar": Pagada o Cancelada con motivo. */
    data class Validate(val sale: AdminStoreSale) : StoreSalesDialog()
}

private fun productOptionLabel(product: AdminProduct): String {
    val price = formatMoney(product.priceCents.toInt(), product.currency)
    val usd = if (product.priceUsdCents > 0) " · ${formatMoney(product.priceUsdCents.toInt(), "USD")}" else ""
    val stock = if (product.stockQty <= 0) " · AGOTADO" else " · stock ${product.stockQty}"
    return "${product.name} · ${product.categoryLabel} · $price$usd$stock"
}

private data class DraftLine(
    val productId: String?,
    val productName: String,
    val qty: Int,
    val unitPriceCents: Long,
    val search: String = productName,
) {
    val lineTotalCents: Long get() = qty.toLong() * unitPriceCents
}

/** Opción unificada del buscador: socio o trabajador (el trabajador se guarda como walk-in). */
private data class StoreSalePersonOption(
    val key: String,
    val name: String,
    val kindLabel: String,
    val clientId: String?,
) {
    val listLabel: String get() = "$name · $kindLabel"
}

private fun storeSalePersonOptions(
    clients: List<AdminClient>,
    workers: List<AdminWorker>,
    query: String,
    limit: Int = 10,
): List<StoreSalePersonOption> {
    val q = query.trim()
    if (q.length < 2) return emptyList()
    val clientHits = clients
        .filter {
            it.fullName.contains(q, ignoreCase = true) ||
                it.docId.orEmpty().contains(q, ignoreCase = true) ||
                it.email.orEmpty().contains(q, ignoreCase = true)
        }
        .map {
            StoreSalePersonOption(
                key = "c:${it.id}",
                name = it.fullName,
                kindLabel = "Socio",
                clientId = it.id,
            )
        }
    val workerHits = workers
        .filter { it.active }
        .filter {
            it.displayName.contains(q, ignoreCase = true) ||
                it.docId.orEmpty().contains(q, ignoreCase = true) ||
                it.email.orEmpty().contains(q, ignoreCase = true)
        }
        .map {
            StoreSalePersonOption(
                key = "w:${it.id}",
                name = it.displayName,
                kindLabel = "Trabajador",
                clientId = null,
            )
        }
    return (clientHits + workerHits).take(limit)
}

@Composable
fun AdminStoreSalesScreen() {
    val scope = rememberCoroutineScope()
    var sales by remember { mutableStateOf<List<AdminStoreSale>>(emptyList()) }
    var products by remember { mutableStateOf<List<AdminProduct>>(emptyList()) }
    var companyId by remember { mutableStateOf<String?>(null) }
    var clients by remember { mutableStateOf<List<AdminClient>>(emptyList()) }
    var workers by remember { mutableStateOf<List<AdminWorker>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var query by remember { mutableStateOf("") }
    val todayDefault = remember { todayIsoDate() }
    var dateFrom by remember { mutableStateOf(formatIsoToDisplayDate(todayDefault)) }
    var dateTo by remember { mutableStateOf(formatIsoToDisplayDate(todayDefault)) }
    var dialog by remember { mutableStateOf<StoreSalesDialog?>(null) }
    var saving by remember { mutableStateOf(false) }
    val pendingApprovals = LocalPendingApprovalNotifier.current
    // Descuento de cobros que quedaron en aprobación: se conserva para volver a cobrar sin reescribirlo.
    val pendingDiscounts = remember { mutableMapOf<String, org.gymly.model.AdminPaymentDiscount>() }
    var successMsg by remember { mutableStateOf<String?>(null) }
    var stockAlert by remember { mutableStateOf<String?>(null) }

    fun reload(silent: Boolean = false, salesOnly: Boolean = false) {
        scope.launch {
            if (!silent) loading = true
            if (!silent) error = null
            try {
                sales = AdminRepo.api.listStoreSales()
                if (!salesOnly) {
                    products = AdminRepo.api.listProducts().filter { it.active }
                    companyId = runCatching { AdminRepo.api.getCompany().id }.getOrElse {
                        AdminRepo.api.listBranches().firstOrNull()?.id
                    }
                    clients = AdminRepo.api.listClients()
                    workers = runCatching { AdminRepo.api.listWorkers() }.getOrDefault(emptyList())
                }
            } catch (e: Exception) {
                if (!silent) {
                    error = e.userMessage("No se pudieron cargar las ventas.")
                }
            } finally {
                if (!silent) loading = false
            }
        }
    }

    fun reloadSalesSilent() {
        scope.launch {
            runCatching {
                sales = AdminRepo.api.listStoreSales()
            }
        }
    }

    fun patchSale(updated: AdminStoreSale) {
        sales = sales.map { if (it.id == updated.id) updated else it }
    }

    LaunchedEffect(Unit) { reload() }
    // Si otra persona resuelve una aprobación (por ejemplo, el administrador), la lista se actualiza sola.
    val dataRevision = LocalAdminDataRevision.current
    LaunchedEffect(dataRevision) {
        if (dataRevision > 0) reload(silent = true)
    }

    val today = remember { todayIsoDate() }

    val filtered = remember(sales, query, dateFrom, dateTo) {
        val q = query.trim().lowercase()
        val fromIso = parseFlexibleDateToIso(dateFrom) ?: today
        val toIso = parseFlexibleDateToIso(dateTo) ?: today
        val rangeStart = minOf(fromIso, toIso)
        val rangeEnd = maxOf(fromIso, toIso)
        sales.filter { s ->
            val day = s.saleDate.take(10)
            val matchesQuery = q.isEmpty() ||
                s.customerName.lowercase().contains(q) ||
                s.itemsSummary.lowercase().contains(q) ||
                s.methodLabel.lowercase().contains(q) ||
                s.statusLabel.lowercase().contains(q) ||
                "#${s.saleNumber}".contains(q)
            val matchesDate = day >= rangeStart && day <= rangeEnd
            matchesQuery && matchesDate
        }
    }

    val listPaging = rememberAdminListPaging(
        initialSortKey = "date",
        initialDir = AdminSortDir.Desc,
        resetKey = listOf(query, dateFrom, dateTo),
    )
    val pageItems = remember(filtered, listPaging.sort, listPaging.page) {
        filtered.adminSortedPaged(listPaging.sort, listPaging.page, STORE_SALES_PAGE_SIZE) { key ->
            when (key) {
                "total" -> { s -> s.totalCents }
                "method" -> { s -> s.methodLabel.lowercase() }
                "origin" -> { s -> s.origin.name }
                "status" -> { s -> s.status.name }
                "sale" -> { s -> s.customerName.lowercase() }
                else -> { s -> s.saleDate }
            }
        }
    }

    val todaySalesCents = filtered
        .filter { it.saleDate.take(10) == today && it.status == AdminStoreSaleStatus.PAID }
        .sumOf { it.totalCents }
    val clientRequests = filtered.count { it.origin == AdminStoreSaleOrigin.CLIENT_AUTOPAY }
    val pending = filtered.count { it.status == AdminStoreSaleStatus.PENDING }
    val totalSales = filtered.size
    val filteredCurrency = filtered.firstOrNull()?.currency ?: GymlyCurrency.CODE
    val salesMoneySummary = remember(filtered, products) { storeSalesMoneySummary(filtered, products) }

    BoxWithConstraints(Modifier.fillMaxSize().background(AdminColors.Surface).padding(GymlySpace.lg)) {
        val compact = maxWidth < AdminLayoutBreakpoints.SIDEBAR_WIDE_MIN.dp
        VerticalScrollColumn(Modifier.fillMaxSize()) {
            StoreSalesHeader()
            Spacer(Modifier.height(16.dp))
            StoreSalesKpis(
                todayLabel = formatMoney(todaySalesCents.toInt().coerceAtLeast(0), GymlyCurrency.CODE),
                clientRequests = clientRequests,
                pending = pending,
                total = totalSales,
                compact = compact,
            )
            Spacer(Modifier.height(16.dp))
            if (saving && dialog == null) {
                Text(
                    "Registrando cobro… no pulses de nuevo.",
                    color = AdminColors.Primary,
                    fontSize = 13.sp,
                    fontWeight = FontWeight.Medium,
                )
                Spacer(Modifier.height(8.dp))
            }
            if (error != null) {
                Text(error!!, color = AdminColors.Error, fontSize = 13.sp)
                Spacer(Modifier.height(8.dp))
            }
            if (successMsg != null) {
                Text(successMsg!!, color = AdminColors.Primary, fontSize = 13.sp)
                Spacer(Modifier.height(8.dp))
            }
            stockAlert?.let { message ->
                AlertDialog(
                    onDismissRequest = { stockAlert = null },
                    title = { Text("Inventario") },
                    text = { Text(message) },
                    confirmButton = {
                        TextButton(onClick = { stockAlert = null }) {
                            Text("Entendido", color = AdminColors.Primary)
                        }
                    },
                )
            }
            // Validar comprobante: solo con licencia de portal o app y permiso de Ventas (Usar).
            val storeSaleValidation: ((AdminStoreSale) -> Unit)? =
                if (rememberProofValidationAllowed() && canWriteModule(AdminModuleAccess.SALES)) {
                    { sale -> dialog = StoreSalesDialog.Validate(sale) }
                } else {
                    null
                }
            CompositionLocalProvider(LocalStoreSaleValidation provides storeSaleValidation) {
                StoreSalesDirectory(
                    sales = pageItems,
                    products = products,
                    totalCount = filtered.size,
                    moneySummary = salesMoneySummary,
                    filteredCurrency = filteredCurrency,
                    listPaging = listPaging,
                    loading = loading,
                    compact = compact,
                    query = query,
                    onQueryChange = { query = it },
                    dateFrom = dateFrom,
                    onDateFromChange = { dateFrom = it },
                    dateTo = dateTo,
                    onDateToChange = { dateTo = it },
                    onCreate = { dialog = StoreSalesDialog.Create },
                    onView = { dialog = StoreSalesDialog.Detail(it) },
                    onInvoice = { sale ->
                        scope.launch {
                            try {
                                val file = AdminRepo.api.downloadStoreSaleInvoice(sale.id)
                                downloadBytes(file.fileName, file.mimeType, file.bytes)
                                successMsg = "Factura #${sale.saleNumber} lista para imprimir."
                                error = null
                            } catch (e: Exception) {
                                error = e.userMessage("No se pudo descargar la factura.")
                            }
                        }
                    },
                    onApprove = { dialog = StoreSalesDialog.Collect(it) },
                    onDelete = { dialog = StoreSalesDialog.Delete(it) },
                )
            }
            Spacer(Modifier.height(12.dp))
            Text(
                "Pendiente: «Cobrar» abre método y referencia → Pagada. " +
                    "Pagada: usa la impresora para la factura; para cambiarla, elimínala y vuelve a crearla. " +
                    "Al registrar, solo queda Pendiente si marcas «Pagar más tarde» o si viene del portal.",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
            )
            Spacer(Modifier.height(8.dp))
        }
    }

    when (val d = dialog) {
        is StoreSalesDialog.Detail -> StoreSaleDetailDialog(
            sale = d.sale,
            onDismiss = { dialog = null },
            onMarkPaid = if (d.sale.status == AdminStoreSaleStatus.PENDING) {
                { dialog = StoreSalesDialog.Collect(d.sale) }
            } else {
                null
            },
            onCancel = if (d.sale.status == AdminStoreSaleStatus.PENDING) {
                {
                    scope.launch {
                        saving = true
                        try {
                            val failed = AdminRepo.api.approveStoreSale(d.sale.id, AdminStoreSaleStatus.FAILED)
                            sales = sales.map { if (it.id == failed.id) failed else it }
                            dialog = null
                            successMsg = "Venta #${d.sale.saleNumber} cancelada."
                        } catch (e: Exception) {
                            error = e.userMessage("No se pudo cancelar la venta.")
                        } finally {
                            saving = false
                        }
                    }
                }
            } else {
                null
            },
            saving = saving,
        )
        is StoreSalesDialog.Collect -> StoreSaleCollectPaymentDialog(
            sale = d.sale,
            products = products,
            initialDiscount = pendingDiscounts[d.sale.id],
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onCollect = { upsert, proof ->
                if (saving) return@StoreSaleCollectPaymentDialog
                val prior = d.sale
                val optimistic = prior.copy(
                    method = upsert.method,
                    methodLabel = upsert.method.labelEs(),
                    status = AdminStoreSaleStatus.PAID,
                    statusLabel = AdminStoreSaleStatus.PAID.labelEs(),
                    notes = upsert.notes,
                    amountPaidCents = upsert.amountPaidCents ?: prior.totalCents,
                    changeGivenCents = upsert.changeGivenCents,
                    foreignUsdCents = upsert.foreignUsdCents,
                )
                upsert.discount?.let { pendingDiscounts[prior.id] = it }
                dialog = null
                patchSale(optimistic)
                scope.launch {
                    saving = true
                    error = null
                    try {
                        var paid = AdminRepo.api.recordStoreSalePayment(prior.id, upsert)
                        if (proof != null) {
                            val withProof = AdminRepo.api.uploadStoreSaleProof(prior.id, proof)
                            if (withProof.proofUrl.isNullOrBlank()) {
                                error = "El cobro se registró pero el comprobante no quedó guardado."
                            } else {
                                paid = withProof
                            }
                        }
                        if (paid.status != AdminStoreSaleStatus.PAID) {
                            paid = AdminRepo.api.approveStoreSale(prior.id, AdminStoreSaleStatus.PAID)
                        }
                        patchSale(paid)
                        successMsg = "Venta #${paid.saleNumber} cobrada."
                        pendingDiscounts.remove(prior.id)
                        reloadSalesSilent()
                    } catch (e: Exception) {
                        patchSale(prior)
                        if (pendingApprovals.offer(e)) {
                            successMsg = "Cobro de venta #${prior.saleNumber} enviado a aprobación. Se aplicará al aprobarlo."
                            error = null
                        } else {
                            successMsg = null
                            error = e.userMessage("No se pudo cobrar la venta.")
                            pendingDiscounts.remove(prior.id)
                        }
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is StoreSalesDialog.Create -> StoreSaleFormDialog(
            title = "Registrar venta",
            serverError = error,
            initial = null,
            products = products,
            companyId = companyId,
            clients = clients,
            workers = workers,
            saving = saving,
            onDismiss = { dialog = null },
            onSave = { upsert, proof ->
                if (saving) return@StoreSaleFormDialog
                scope.launch {
                    saving = true
                    // Cada envío empieza sin el error del intento anterior.
                    error = null
                    try {
                        val depleted = inventoryAlertNames(upsert.items, products)
                        var created = AdminRepo.api.createStoreSale(upsert)
                        if (proof != null) {
                            val withProof = AdminRepo.api.uploadStoreSaleProof(created.id, proof)
                            if (withProof.proofUrl.isNullOrBlank()) {
                                error = "La venta se creó pero el comprobante no quedó guardado."
                                sales = listOf(created) + sales.filterNot { it.id == created.id }
                                return@launch
                            }
                            created = withProof
                        }
                        // Defensa: si el backend devolviera Pendiente tras un cobro, confirmar.
                        if (upsert.status == AdminStoreSaleStatus.PAID &&
                            created.status == AdminStoreSaleStatus.PENDING
                        ) {
                            created = AdminRepo.api.approveStoreSale(created.id, AdminStoreSaleStatus.PAID)
                        }
                        sales = listOf(created) + sales.filterNot { it.id == created.id }
                        dialog = null
                        successMsg = "Venta #${created.saleNumber} registrada."
                        stockAlert = inventoryAlertMessage(depleted)
                    } catch (e: Exception) {
                        // Si quedó en aprobación, el formulario se cierra: así no se reenvía y se duplica la solicitud.
                        if (pendingApprovals.offer(e)) {
                            dialog = null
                            error = null
                            successMsg = "Venta enviada a aprobación. Se creará al aprobarla."
                        } else {
                            error = e.userMessage("No se pudo registrar la venta.")
                        }
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is StoreSalesDialog.Validate -> ProofValidateDialog(
            amountLabel = "Venta #${d.sale.saleNumber} · ${d.sale.customerName} · " +
                formatMoney(d.sale.displayTotalCents().toInt(), d.sale.currency),
            reference = d.sale.notes,
            paymentDateIso = d.sale.saleDate,
            excludeStoreSaleId = d.sale.id,
            onDismiss = { dialog = null },
            onDecide = { paid, reason ->
                try {
                    AdminRepo.api.validateStoreSaleProof(d.sale.id, paid, reason)
                    successMsg = if (paid) {
                        "Venta #${d.sale.saleNumber} validada como pagada."
                    } else {
                        "Venta #${d.sale.saleNumber} cancelada por comprobante rechazado."
                    }
                    error = null
                } catch (e: AdminPendingApprovalException) {
                    successMsg = e.message
                    error = null
                }
                reloadSalesSilent()
            },
        )
        is StoreSalesDialog.Delete -> AlertDialog(
            onDismissRequest = { if (!saving) dialog = null },
            title = { Text("Eliminar venta") },
            text = { Text("¿Eliminar la venta #${d.sale.saleNumber}? Esto no se puede deshacer.") },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            try {
                                AdminRepo.api.deleteStoreSale(d.sale.id)
                                dialog = null
                                reload()
                            } catch (e: Exception) {
                                error = e.userMessage("No se pudo eliminar la venta.")
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(containerColor = AdminColors.Primary),
                ) { Text("Eliminar") }
            },
            dismissButton = {
                TextButton(onClick = { dialog = null }, enabled = !saving) { Text("Cancelar") }
            },
        )
        null -> Unit
    }
}

@Composable
private fun StoreSalesHeader() {
    Column(Modifier.fillMaxWidth()) {
        Text("Ventas de la tienda", fontSize = 24.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
        Spacer(Modifier.height(4.dp))
        Text(
            "Solicitudes de compra de clientes y ventas registradas por el personal — POP, equipamiento y alimentos/suplementos.",
            color = AdminColors.TextMuted,
            fontSize = 13.sp,
        )
    }
}

@Composable
private fun StoreSalesKpis(
    todayLabel: String,
    clientRequests: Int,
    pending: Int,
    total: Int,
    compact: Boolean,
) {
    val cards = listOf(
        Triple("Ventas de hoy", todayLabel, Color(0xFF2E7D32)),
        Triple("Solicitudes de clientes", clientRequests.toString(), Color(0xFF6A1B9A)),
        Triple("Pendientes", pending.toString(), Color(0xFFE65100)),
        Triple("Total de ventas", total.toString(), AdminColors.Primary),
    )
    if (compact) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            cards.chunked(2).forEach { row ->
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                    row.forEach { (label, value, accent) ->
                        StoreSaleKpiCard(label, value, accent, Modifier.weight(1f))
                    }
                    if (row.size == 1) Spacer(Modifier.weight(1f))
                }
            }
        }
    } else {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            cards.forEach { (label, value, accent) ->
                StoreSaleKpiCard(label, value, accent, Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun StoreSaleKpiCard(label: String, value: String, accent: Color, modifier: Modifier = Modifier) {
    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .background(AdminColors.SurfaceContainer)
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.45f), RoundedCornerShape(14.dp))
            .padding(16.dp),
    ) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(8.dp))
        Text(value, color = accent, fontWeight = FontWeight.Bold, fontSize = 22.sp)
    }
}

@Composable
private fun StoreSalesDirectory(
    sales: List<AdminStoreSale>,
    products: List<AdminProduct>,
    totalCount: Int,
    moneySummary: StoreSalesMoneySummary,
    filteredCurrency: String,
    listPaging: AdminListPaging,
    loading: Boolean,
    compact: Boolean,
    query: String,
    onQueryChange: (String) -> Unit,
    dateFrom: String,
    onDateFromChange: (String) -> Unit,
    dateTo: String,
    onDateToChange: (String) -> Unit,
    onCreate: () -> Unit,
    onView: (AdminStoreSale) -> Unit,
    onInvoice: (AdminStoreSale) -> Unit,
    onApprove: (AdminStoreSale) -> Unit,
    onDelete: (AdminStoreSale) -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.45f), RoundedCornerShape(16.dp))
            .padding(16.dp),
    ) {
        if (compact) {
            Text("Ventas", fontWeight = FontWeight.Bold, fontSize = 16.sp)
            Spacer(Modifier.height(10.dp))
            StoreSaleDateRangeRow(dateFrom, onDateFromChange, dateTo, onDateToChange)
            Spacer(Modifier.height(10.dp))
            StoreSaleSearchField(query, onQueryChange)
            Spacer(Modifier.height(10.dp))
            Button(
                onClick = onCreate,
                modifier = Modifier.fillMaxWidth(),
                colors = ButtonDefaults.buttonColors(containerColor = AdminColors.Primary),
            ) {
                Text("+ Registrar venta", fontWeight = FontWeight.SemiBold)
            }
        } else {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("Ventas", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.Bottom) {
                    StoreSaleDateRangeRow(dateFrom, onDateFromChange, dateTo, onDateToChange)
                    Box(Modifier.widthIn(max = 220.dp)) { StoreSaleSearchField(query, onQueryChange) }
                    Button(
                        onClick = onCreate,
                        colors = ButtonDefaults.buttonColors(containerColor = AdminColors.Primary),
                    ) {
                        Text("+ Registrar venta", fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
        Spacer(Modifier.height(14.dp))
        when {
            loading -> Box(Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            totalCount == 0 -> {
                Text("No hay ventas en el rango seleccionado.", color = AdminColors.TextMuted)
                Spacer(Modifier.height(12.dp))
                StoreSalesTotalsRow(
                    totalCount = 0,
                    moneySummary = StoreSalesMoneySummary(),
                    currency = filteredCurrency,
                )
            }
            compact -> {
                Column(
                    Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    sales.forEach { s ->
                        StoreSaleMobileCard(s, products, onView, onInvoice, onApprove, onDelete)
                    }
                }
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                    pageSize = STORE_SALES_PAGE_SIZE,
                )
                Spacer(Modifier.height(8.dp))
                StoreSalesTotalsRow(
                    totalCount = totalCount,
                    moneySummary = moneySummary,
                    currency = filteredCurrency,
                )
            }
            else -> {
                StoreSalesTable(
                    sales = sales,
                    products = products,
                    listPaging = listPaging,
                    totalCount = totalCount,
                    moneySummary = moneySummary,
                    filteredCurrency = filteredCurrency,
                    onView = onView,
                    onInvoice = onInvoice,
                    onApprove = onApprove,
                    onDelete = onDelete,
                    modifier = Modifier.fillMaxWidth(),
                )
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                    pageSize = STORE_SALES_PAGE_SIZE,
                )
            }
        }
    }
}

@Composable
private fun StoreSaleDateRangeRow(
    dateFrom: String,
    onDateFromChange: (String) -> Unit,
    dateTo: String,
    onDateToChange: (String) -> Unit,
) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(16.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        AdminDateField(
            value = dateFrom,
            onValueChange = onDateFromChange,
            modifier = Modifier.width(AdminDateFieldFilterWidth),
            label = "Desde",
            placeholder = "DD-MM-AAAA",
            outputFormat = AdminDateOutputFormat.DISPLAY,
        )
        Text(
            "–",
            color = AdminColors.TextMuted,
            fontSize = 16.sp,
            modifier = Modifier.padding(top = 18.dp),
        )
        AdminDateField(
            value = dateTo,
            onValueChange = onDateToChange,
            modifier = Modifier.width(AdminDateFieldFilterWidth),
            label = "Hasta",
            placeholder = "DD-MM-AAAA",
            outputFormat = AdminDateOutputFormat.DISPLAY,
        )
    }
}

private data class StoreSaleMethodMoney(
    val method: AdminStoreSaleMethod,
    val label: String,
    val totalCents: Long,
    val currency: String,
    val paidCents: Long = 0L,
    val pendingCents: Long = 0L,
)

private data class StoreSalesMoneySummary(
    val methods: List<StoreSaleMethodMoney> = emptyList(),
    val pendingBsCents: Long = 0L,
    val pendingUsdCents: Long = 0L,
    val changeGivenCents: Long = 0L,
    val currency: String = GymlyCurrency.CODE,
) {
    val hasContent: Boolean
        get() = methods.isNotEmpty() ||
            pendingBsCents > 0L ||
            pendingUsdCents > 0L ||
            changeGivenCents > 0L
}

/** Cobro de una sola moneda (todo en Bs o todo en $). Con pagos mixtos no se fuerza ningún pendiente a 0,00. */
private fun cobroEsDeUnaMoneda(lines: List<PaymentLineDraft>): Boolean {
    val conMonto = lines.filter { parseBulkAmountToCents(it.amountInput) > 0L }
    return conMonto.all { it.method.usesUsdAmount() } || conMonto.none { it.method.usesUsdAmount() }
}

private fun storeSalesMoneySummary(
    sales: List<AdminStoreSale>,
    products: List<AdminProduct> = emptyList(),
): StoreSalesMoneySummary {
    val byMethod = linkedMapOf<AdminStoreSaleMethod, StoreSaleMethodMoney>()
    var pendingBs = 0L
    var pendingUsd = 0L
    var change = 0L
    var currency = GymlyCurrency.CODE

    sales.forEach { sale ->
        if (sale.currency.isNotBlank()) currency = sale.currency
        val displayCents = sale.displayTotalCents(products).coerceAtLeast(0L)
        val displayCurrency = sale.displayTotalCurrency()
        val existing = byMethod[sale.method]
        val paidAdd = if (sale.status == AdminStoreSaleStatus.PAID) displayCents else 0L
        val pendingAdd = if (sale.status == AdminStoreSaleStatus.PENDING) displayCents else 0L
        byMethod[sale.method] = if (existing == null) {
            StoreSaleMethodMoney(
                method = sale.method,
                label = sale.methodLabel.ifBlank { sale.method.labelEs() },
                totalCents = displayCents,
                currency = displayCurrency,
                paidCents = paidAdd,
                pendingCents = pendingAdd,
            )
        } else {
            existing.copy(
                totalCents = existing.totalCents + displayCents,
                paidCents = existing.paidCents + paidAdd,
                pendingCents = existing.pendingCents + pendingAdd,
                currency = if (existing.currency == "USD" || displayCurrency == "USD") "USD" else existing.currency,
            )
        }
        if (sale.status == AdminStoreSaleStatus.PENDING) {
            if (sale.method.usesUsdAmount()) pendingUsd += displayCents else pendingBs += displayCents
        }
        if (sale.status == AdminStoreSaleStatus.PAID) {
            change += sale.changeGivenCents.coerceAtLeast(0L)
        }
    }

    val preferred = AdminStoreSaleMethod.entries
    val methods = byMethod.values
        .filter { it.totalCents > 0L }
        .sortedBy { m -> preferred.indexOf(m.method).takeIf { it >= 0 } ?: preferred.size }

    return StoreSalesMoneySummary(
        methods = methods,
        pendingBsCents = pendingBs,
        pendingUsdCents = pendingUsd,
        changeGivenCents = change,
        currency = currency,
    )
}

/** Precio real (Bs y $), lo pagado en cada moneda y el vuelto de una venta. Va debajo de su descripción. */
private fun storeSaleMoneyDetail(sale: AdminStoreSale, products: List<AdminProduct>): String {
    val usdPrice = sale.items.sumOf { item ->
        val unitUsd = products.firstOrNull { it.id == item.productId }?.priceUsdCents ?: 0L
        unitUsd.coerceAtLeast(0L) * item.qty.coerceAtLeast(0)
    }
    val paidUsd = sale.payments.filter { it.currency == "USD" }.sumOf { it.amountCents }
    val paidBs = sale.payments.filter { it.currency != "USD" }.sumOf { it.amountCents }
    return buildList {
        add("Precio ${formatMoney(sale.totalCents.toInt(), sale.currency)}")
        if (usdPrice > 0L) add(formatMoney(usdPrice.toInt(), "USD"))
        if (paidUsd > 0L) add("Pagó ${formatMoney(paidUsd.toInt(), "USD")}")
        if (paidBs > 0L) add("Pagó ${formatMoney(paidBs.toInt(), sale.currency)}")
        if (sale.changeGivenCents > 0L) add("Vuelto ${formatMoney(sale.changeGivenCents.toInt(), sale.currency)}")
        if (sale.discountCents > 0L) add("Descuento ${formatMoney(sale.discountCents.toInt(), GymlyCurrency.CODE)}")
    }.joinToString(" · ")
}

@Composable
private fun StoreSalesTotalsRow(
    totalCount: Int,
    moneySummary: StoreSalesMoneySummary,
    currency: String,
    modifier: Modifier = Modifier,
) {
    val chips = buildList {
        moneySummary.methods.forEach { method ->
            val amount = formatMoney(method.totalCents.toInt(), method.currency)
            val subtitle = buildList {
                if (method.paidCents > 0L && method.paidCents != method.totalCents) {
                    add("Pagadas ${formatMoney(method.paidCents.toInt(), method.currency)}")
                }
                if (method.pendingCents > 0L) {
                    add("Pend. ${formatMoney(method.pendingCents.toInt(), method.currency)}")
                }
            }.joinToString(" · ")
            add(Triple(method.label, amount to subtitle.ifBlank { null }, AdminColors.Primary))
        }
        if (moneySummary.pendingBsCents > 0L || moneySummary.pendingUsdCents > 0L) {
            val pending = buildList {
                if (moneySummary.pendingUsdCents > 0L) {
                    add(formatMoney(moneySummary.pendingUsdCents.toInt(), "USD"))
                }
                if (moneySummary.pendingBsCents > 0L) {
                    add(formatMoney(moneySummary.pendingBsCents.toInt(), currency))
                }
            }.joinToString(" · ")
            add(Triple("Pendientes", pending to null, Color(0xFFE65100)))
        }
        if (moneySummary.changeGivenCents > 0L) {
            add(
                Triple(
                    "Vuelto",
                    formatMoney((-moneySummary.changeGivenCents).toInt(), currency) to null,
                    Color(0xFF7C3AED),
                ),
            )
        }
    }

    Column(
        modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(AdminColors.Primary.copy(alpha = 0.08f))
            .padding(vertical = 10.dp, horizontal = 12.dp),
    ) {
        Text(
            "Totales · $totalCount ${if (totalCount == 1) "venta" else "ventas"}",
            fontWeight = FontWeight.Bold,
            fontSize = 13.sp,
            color = AdminColors.Primary,
        )
        Spacer(Modifier.height(8.dp))
        if (chips.isEmpty()) {
            Text("Sin montos en el rango.", color = AdminColors.TextMuted, fontSize = 12.sp)
        } else {
            Row(
                Modifier.horizontalScroll(rememberScrollState()),
                horizontalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                chips.forEach { (label, amountAndSub, color) ->
                    val (amount, subtitle) = amountAndSub
                    key(label, amount) {
                        Column(
                            Modifier
                                .widthIn(min = 120.dp)
                                .clip(RoundedCornerShape(10.dp))
                                .background(AdminColors.Surface)
                                .border(1.dp, AdminColors.Outline.copy(alpha = 0.4f), RoundedCornerShape(10.dp))
                                .padding(horizontal = 12.dp, vertical = 8.dp),
                        ) {
                            Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
                            Text(amount, fontWeight = FontWeight.Bold, fontSize = 14.sp, color = color)
                            if (!subtitle.isNullOrBlank()) {
                                Text(subtitle, color = AdminColors.TextMuted, fontSize = 10.sp)
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun StoreSaleSearchField(query: String, onQueryChange: (String) -> Unit) {
    OutlinedTextField(
        value = query,
        onValueChange = onQueryChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text("Buscar...", color = AdminColors.TextMuted) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}

@Composable
private fun StoreSalesTable(
    sales: List<AdminStoreSale>,
    products: List<AdminProduct>,
    listPaging: AdminListPaging,
    totalCount: Int,
    moneySummary: StoreSalesMoneySummary,
    filteredCurrency: String,
    onView: (AdminStoreSale) -> Unit,
    onInvoice: (AdminStoreSale) -> Unit,
    onApprove: (AdminStoreSale) -> Unit,
    onDelete: (AdminStoreSale) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth()) {
            Row(
                Modifier.fillMaxWidth().padding(vertical = 8.dp, horizontal = 4.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(4.dp),
            ) {
                AdminSortableHeader("Venta", "sale", listPaging.sort, listPaging::onSort, Modifier.weight(1.5f))
                AdminSortableHeader("Total", "total", listPaging.sort, listPaging::onSort, Modifier.weight(0.75f))
                AdminSortableHeader("Método", "method", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
                AdminSortableHeader("Origen", "origin", listPaging.sort, listPaging::onSort, Modifier.weight(1f))
                AdminSortableHeader("Fecha", "date", listPaging.sort, listPaging::onSort, Modifier.weight(0.85f))
                AdminSortableHeader("Estado", "status", listPaging.sort, listPaging::onSort, Modifier.weight(0.8f))
                AdminListHeaderLabel("Acciones", Modifier.width(240.dp))
            }
            HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
            sales.forEach { s ->
                Row(
                    Modifier.fillMaxWidth().padding(vertical = 10.dp, horizontal = 4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    Row(Modifier.weight(1.5f), verticalAlignment = Alignment.CenterVertically) {
                        Box(
                            Modifier.size(8.dp).clip(CircleShape).background(Color(s.status.dotColorArgb())),
                        )
                        Spacer(Modifier.width(10.dp))
                        Column {
                            Text(
                                "#${s.saleNumber} · ${s.customerName}",
                                fontWeight = FontWeight.SemiBold,
                                color = AdminColors.Text,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis,
                            )
                            Text(s.itemsSummary, color = AdminColors.TextMuted, fontSize = 12.sp, maxLines = 1, overflow = TextOverflow.Ellipsis)
                            Text(storeSaleMoneyDetail(s, products), color = AdminColors.TextMuted, fontSize = 12.sp, maxLines = 1, overflow = TextOverflow.Ellipsis)
                            val pendingLabel = listOfNotNull(
                                s.pendingBsCents.takeIf { it > 0L }?.let { formatMoney(it.toInt(), s.currency) },
                                s.pendingUsdCents.takeIf { it > 0L }?.let { formatMoney(it.toInt(), "USD") },
                            ).joinToString(" · ")
                            if (pendingLabel.isNotBlank()) {
                                Text("Pendiente $pendingLabel", color = Color(0xFFE65100), fontSize = 12.sp)
                            }
                        }
                    }
                    Text(
                        s.displayTotalLabel(products),
                        Modifier.weight(0.75f),
                        color = AdminColors.Text,
                        fontSize = 13.sp,
                        maxLines = 1,
                    )
                    Column(Modifier.weight(0.9f)) {
                        Text(s.methodLabel, color = AdminColors.Text, fontSize = 13.sp, maxLines = 1)
                        // Pago móvil: la referencia en azul, como en el cuadre de caja de reportes.
                        if (s.method == AdminStoreSaleMethod.MOBILE_PAYMENT) {
                            s.notes?.takeIf { it.isNotBlank() }?.let { ref ->
                                Text(
                                    ref,
                                    color = AdminColors.Primary,
                                    fontSize = 12.sp,
                                    fontWeight = FontWeight.Medium,
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis,
                                )
                            }
                        }
                    }
                    Box(Modifier.weight(1f)) { OriginBadge(s.origin) }
                    Text(s.saleDate, Modifier.weight(0.85f), color = AdminColors.Text, fontSize = 13.sp, maxLines = 1)
                    Box(Modifier.weight(0.8f)) { SaleStatusBadge(s.status) }
                    Box(Modifier.width(240.dp)) {
                        StoreSaleActionIcons(
                            sale = s,
                            onView = onView,
                            onInvoice = onInvoice,
                            onApprove = onApprove,
                            onDelete = onDelete,
                        )
                    }
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
            }
        }
        Spacer(Modifier.height(6.dp))
        StoreSalesTotalsRow(
            totalCount = totalCount,
            moneySummary = moneySummary,
            currency = filteredCurrency,
        )
    }
}

@Composable
private fun StoreSaleMobileCard(
    sale: AdminStoreSale,
    products: List<AdminProduct>,
    onView: (AdminStoreSale) -> Unit,
    onInvoice: (AdminStoreSale) -> Unit,
    onApprove: (AdminStoreSale) -> Unit,
    onDelete: (AdminStoreSale) -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.6f), RoundedCornerShape(12.dp))
            .padding(12.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Box(Modifier.size(8.dp).clip(CircleShape).background(Color(sale.status.dotColorArgb())))
            Spacer(Modifier.width(10.dp))
            Column(Modifier.weight(1f)) {
                Text(
                    "#${sale.saleNumber} · ${sale.customerName}",
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(sale.itemsSummary, color = AdminColors.TextMuted, fontSize = 12.sp, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text(storeSaleMoneyDetail(sale, products), color = AdminColors.TextMuted, fontSize = 12.sp, maxLines = 1, overflow = TextOverflow.Ellipsis)
            }
            SaleStatusBadge(sale.status)
        }
        Spacer(Modifier.height(8.dp))
        Text(sale.displayTotalLabel(products), fontSize = 13.sp)
        Text("${sale.methodLabel} · ${sale.saleDate}", color = AdminColors.TextMuted, fontSize = 12.sp)
        if (sale.method == AdminStoreSaleMethod.MOBILE_PAYMENT) {
            sale.notes?.takeIf { it.isNotBlank() }?.let { ref ->
                Text(ref, color = AdminColors.Primary, fontSize = 12.sp, fontWeight = FontWeight.Medium)
            }
        }
        Spacer(Modifier.height(4.dp))
        OriginBadge(sale.origin)
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
            StoreSaleActionIcons(
                sale = sale,
                onView = onView,
                onInvoice = onInvoice,
                onApprove = onApprove,
                onDelete = onDelete,
            )
        }
    }
}

/**
 * Pendiente: Cobrar + Eliminar + ⋮ (ver).
 * Pagada: Imprimir factura + Eliminar + ⋮ (ver).
 */
@Composable
private fun StoreSaleActionIcons(
    sale: AdminStoreSale,
    onView: (AdminStoreSale) -> Unit,
    onInvoice: (AdminStoreSale) -> Unit,
    onApprove: (AdminStoreSale) -> Unit,
    onDelete: (AdminStoreSale) -> Unit,
) {
    val pending = sale.status == AdminStoreSaleStatus.PENDING
    Row(verticalAlignment = Alignment.CenterVertically) {
        IconButton(onClick = { onView(sale) }) {
            Icon(Icons.Outlined.Search, "Ver detalle", tint = AdminColors.Primary)
        }
        LocalStoreSaleValidation.current?.let { validate ->
            ProofValidateButton(enabled = sale.status == AdminStoreSaleStatus.POR_VALIDAR) { validate(sale) }
        }
        if (sale.status == AdminStoreSaleStatus.PAID) {
            IconButton(onClick = { onInvoice(sale) }) {
                Icon(Icons.Outlined.Print, "Imprimir factura", tint = AdminColors.Primary)
            }
        }
        if (pending && canWriteModule(AdminModuleAccess.SALES)) {
            IconButton(onClick = { onApprove(sale) }) {
                Icon(Icons.Outlined.Payments, "Cobrar (elegir método de pago)", tint = Color(0xFF2E7D32))
            }
        }
        // Eliminar (con permiso de borrar) sirve para ventas pagadas y pendientes.
        if (canDeleteModule(AdminModuleAccess.SALES)) {
            IconButton(onClick = { onDelete(sale) }) {
                Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
            }
        }
    }
}

@Composable
private fun OriginBadge(origin: AdminStoreSaleOrigin) {
    val (fg, bg) = when (origin) {
        AdminStoreSaleOrigin.CLIENT_AUTOPAY -> Color(0xFF1565C0) to Color(0xFFE3F2FD)
        AdminStoreSaleOrigin.STAFF_REGISTERED -> AdminColors.TextMuted to AdminColors.Outline.copy(alpha = 0.25f)
    }
    Text(
        origin.labelEs(),
        color = fg,
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(bg)
            .padding(horizontal = 8.dp, vertical = 4.dp),
    )
}

@Composable
private fun SaleStatusBadge(status: AdminStoreSaleStatus) {
    val (fg, bg) = when (status) {
        AdminStoreSaleStatus.PAID -> Color(0xFF1B5E20) to Color(0xFFE8F5E9)
        AdminStoreSaleStatus.PENDING -> Color(0xFFE65100) to Color(0xFFFFF3E0)
        AdminStoreSaleStatus.FAILED -> Color(0xFFB71C1C) to Color(0xFFFFEBEE)
        AdminStoreSaleStatus.POR_VALIDAR -> Color(0xFF1565C0) to Color(0xFFE3F2FD)
    }
    Text(
        status.labelEs(),
        color = fg,
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(bg)
            .padding(horizontal = 10.dp, vertical = 4.dp),
    )
}

/** Fecha de pago de una venta: la de la venta; si no tiene, la de hoy. Formato yyyy-MM-dd. */
internal fun fechaPagoVenta(saleDate: String, hoy: String): String = saleDate.take(10).ifBlank { hoy }

@Composable
private fun StoreSaleCollectPaymentDialog(
    sale: AdminStoreSale,
    products: List<AdminProduct>,
    saving: Boolean,
    onDismiss: () -> Unit,
    onCollect: (AdminStoreSaleUpsert, PickedImagePayload?) -> Unit,
    initialDiscount: org.gymly.model.AdminPaymentDiscount? = null,
) {
    // Con saldo (abonos previos) se cobra el saldo pendiente, no el total de la venta.
    val hasSaldo = sale.pendingBsCents > 0L || sale.pendingUsdCents > 0L
    val dueBsCents = if (hasSaldo) sale.pendingBsCents else sale.totalCents
    val dueInput = centsToAmountInput(dueBsCents)
    val catalogUsdCents = remember(sale.id, products) {
        sale.items.sumOf { item ->
            val unitUsd = products.firstOrNull { it.id == item.productId }?.priceUsdCents ?: 0L
            unitUsd.coerceAtLeast(0L) * item.qty.coerceAtLeast(0)
        }
    }
    // Lo que falta cobrar en $: el saldo si hay abono previo; si no, el precio de los productos.
    val dueUsdForCobro = if (hasSaldo) sale.pendingUsdCents else catalogUsdCents
    val defaultUsdAmount = dueUsdForCobro.takeIf { it > 0 }?.let { centsToAmountInput(it) }.orEmpty()
    // Seed estable: no pasar paymentLines como initialLines (resetearía el form en cada tecla).
    val initialPaymentLines = remember(sale.id, defaultUsdAmount) {
        val usdMethod = sale.method.usesUsdAmount()
        listOf(
            PaymentLineDraft(
                method = sale.method.toPaymentMethod(),
                // En divisas el monto inicial es solo el de $ (vacío si no hay saldo ni precio en $); nunca el de Bs.
                amountInput = if (usdMethod) defaultUsdAmount else dueInput,
                notes = sale.notes.orEmpty(),
            ),
        )
    }
    var paymentLines by remember(sale.id) { mutableStateOf(initialPaymentLines) }
    var changeGivenCents by remember(sale.id) { mutableStateOf(0L) }
    // Fecha de pago: por defecto la de la venta (o la de hoy si es nueva); el operador puede cambiarla.
    var fechaPago by remember(sale.id) { mutableStateOf(fechaPagoVenta(sale.saleDate, todayIsoDate())) }
    var discountBsInput by remember(sale.id) {
        mutableStateOf(initialDiscount?.let { centsToAmountInput(it.cents) }.orEmpty())
    }
    var discountUsdInput by remember(sale.id) {
        mutableStateOf(initialDiscount?.usdCents?.let { centsToAmountInput(it) }.orEmpty())
    }
    var discountReason by remember(sale.id) { mutableStateOf(initialDiscount?.reason.orEmpty()) }
    var discountNote by remember(sale.id) { mutableStateOf(initialDiscount?.note.orEmpty()) }
    var formError by remember { mutableStateOf<String?>(null) }
    var exchangeQuote by remember { mutableStateOf<org.gymly.model.AdminExchangeRateQuote?>(null) }
    var exchangeRatesOn by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) {
        exchangeRatesOn = runCatching { AdminRepo.api.getSettingsModules().exchangeRatesEnabled }.getOrDefault(false)
        // Con el módulo de tasas apagado la cotización no se usa: ni hint ni conversiones.
        exchangeQuote = if (exchangeRatesOn) runCatching { AdminRepo.api.currentExchangeRate() }.getOrNull() else null
    }
    var walletOn by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) {
        walletOn = runCatching { AdminRepo.api.getSettingsModules().clientAccountsEnabled }.getOrDefault(false)
    }

    Dialog(
        onDismissRequest = { if (!saving) onDismiss() },
        properties = DialogProperties(dismissOnClickOutside = false),
    ) {
        VerticalScrollColumn(
            Modifier
                .widthIn(max = 560.dp)
                .fillMaxWidth()
                .heightIn(max = 640.dp)
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Text("Cobrar venta", fontWeight = FontWeight.Bold, fontSize = 18.sp)
            Spacer(Modifier.height(6.dp))
            Text(
                "#${sale.saleNumber} · ${sale.customerName}",
                fontWeight = FontWeight.SemiBold,
                fontSize = 14.sp,
            )
            Text(sale.itemsSummary, color = AdminColors.TextMuted, fontSize = 12.sp)
            Text(
                buildString {
                    append("Total: ${formatMoney(sale.totalCents.toInt(), sale.currency)}")
                    if (catalogUsdCents > 0) append(" · ${formatMoney(catalogUsdCents.toInt(), "USD")}")
                },
                fontWeight = FontWeight.Bold,
                fontSize = 15.sp,
                color = AdminColors.Primary,
                modifier = Modifier.padding(top = 6.dp),
            )
            Spacer(Modifier.height(12.dp))
            Text(
                "Elige el método con el que paga ahora y registra el cobro.",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
            )
            Spacer(Modifier.height(10.dp))
            CombinedPaymentLinesForm(
                // Con descuento, el total de cobertura es el neto (monto menos descuento).
                totalAmountInput = if (discountLiveBsCents(
                        parseBulkAmountToCents(discountBsInput),
                        parseBulkAmountToCents(discountUsdInput),
                        exchangeQuote,
                        exchangeRatesOn,
                    ) > 0L
                ) {
                    centsToAmountInput((dueBsCents - discountLiveBsCents(
                        parseBulkAmountToCents(discountBsInput),
                        parseBulkAmountToCents(discountUsdInput),
                        exchangeQuote,
                        exchangeRatesOn,
                    )).coerceAtLeast(0L))
                } else {
                    dueInput
                },
                defaultLineAmount = dueInput,
                defaultUsdAmount = defaultUsdAmount,
                enabled = !saving,
                allowOverpay = true,
                enforcePlanCoverage = true,
                checkExistingMobilePayments = true,
                paymentDateIso = fechaPago,
                paymentDateEditable = true,
                onPaymentDateChange = { fechaPago = it },
                clientId = sale.clientId,
                initialLines = initialPaymentLines,
                initialChangeGivenCents = 0L,
                onChangeGivenCentsChange = { changeGivenCents = it },
                onLinesChange = { paymentLines = it },
            ) { _, _, changeGiven, _ ->
                changeGivenCents = changeGiven
            }
            // Descuento justo debajo de la casilla del monto a cobrar (venta nueva o con saldo).
            Spacer(Modifier.height(12.dp))
            BulkDiscountSection(
                bsInput = discountBsInput,
                onBsInputChange = { discountBsInput = it },
                usdInput = discountUsdInput,
                onUsdInputChange = { discountUsdInput = it },
                reason = discountReason,
                onReasonChange = { discountReason = it },
                note = discountNote,
                onNoteChange = { discountNote = it },
                enabled = !saving,
            )
            // Excedente en $ sobre la deuda en $ (pendiente previo o precio del catálogo). No hay vuelto en $.
            val dueUsdCents = if (sale.pendingUsdCents > 0L) sale.pendingUsdCents else catalogUsdCents
            val typedUsdCents = paymentLines.sumOf {
                if (it.method.usesUsdAmount()) foreignCurrencyLineUsdCents(it, dueInput, exchangeQuote) ?: 0L else 0L
            }
            val usdExcessCents = if (dueUsdCents > 0L) (typedUsdCents - dueUsdCents).coerceAtLeast(0L) else 0L
            if (usdExcessCents > 0L) {
                Spacer(Modifier.height(8.dp))
                val excessText = formatMoney(usdExcessCents.toInt(), "USD")
                Text(
                    if (walletOn && sale.clientId != null) {
                        "Excedente $excessText: se acredita al monedero del cliente."
                    } else {
                        "Excedente $excessText: se registra como entregado en USD; el vuelto se declara aparte (en Bs)."
                    },
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
            }
            formError?.let {
                Spacer(Modifier.height(8.dp))
                Text(it, color = AdminColors.Error, fontSize = 12.sp)
            }
            Spacer(Modifier.height(16.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                OutlinedButton(onClick = onDismiss, enabled = !saving) { Text("Cancelar") }
                Spacer(Modifier.width(8.dp))
                Button(
                    onClick = {
                        val lines = paymentLines.ifEmpty {
                            listOf(PaymentLineDraft(amountInput = dueInput))
                        }
                        for (line in lines) {
                            paymentReferenceValidationError(line)?.let {
                                formError = it
                                return@Button
                            }
                        }
                        val (discount, discountError) = bulkDiscountOrError(
                            bsInput = discountBsInput,
                            reason = discountReason,
                            note = discountNote,
                            hasPending = false,
                            hasWalletCredit = false,
                            hasLines = lines.isNotEmpty(),
                            usdInput = discountUsdInput,
                        )
                        if (discountError != null) {
                            formError = discountError
                            return@Button
                        }
                        // Con descuento, lo que se cobra en Bs es el total menos el descuento.
                        val netDueBsCents = (dueBsCents - (discount?.cents ?: 0L)).coerceAtLeast(0L)
                        val netTotalCents = (sale.totalCents - (discount?.cents ?: 0L)).coerceAtLeast(0L)
                        // Tasa apagada: un descuento escrito a mano (en Bs o en $) cierra la venta; el operador lo declara.
                        val descuentoManual = !exchangeRatesOn &&
                            ((discount?.cents ?: 0L) > 0L || (discount?.usdCents ?: 0L) > 0L)
                        val sum = paymentLinesBsCents(lines, dueInput, exchangeQuote)
                        val paidUsd = lines.sumOf {
                            if (it.method.usesUsdAmount()) {
                                foreignCurrencyLineUsdCents(it, dueInput, exchangeQuote) ?: 0L
                            } else {
                                0L
                            }
                        }
                        val usdDueCents = if (hasSaldo) sale.pendingUsdCents else catalogUsdCents
                        val planUsdForValidation = when {
                            usdDueCents > 0L -> usdDueCents
                            paidUsd > 0L && sum < dueBsCents -> paidUsd
                            else -> 0L
                        }
                        if (!hasSaldo && !descuentoManual) paymentSumValidationError(
                            sumCents = sum,
                            targetCents = netTotalCents,
                            allowOverpay = true,
                            enforcePlanCoverage = true,
                            paidUsdCents = paidUsd,
                            planUsdCents = planUsdForValidation,
                        )?.let {
                            formError = it
                            return@Button
                        }
                        val usdCovers = paidUsd > 0L &&
                            (usdDueCents <= 0L || paidUsd >= usdDueCents)
                        val mixedCurrencies = isMixedCurrencyPayment(sum, paidUsd)
                        if (hasSaldo) {
                            val bsShort = sum < netDueBsCents
                            val usdShort = usdDueCents > 0L && paidUsd < usdDueCents
                            if ((bsShort || usdShort) && !mixedCurrencies) {
                                val saldoText = buildList {
                                    if (netDueBsCents > 0L) add(formatMoney(netDueBsCents.toInt(), sale.currency))
                                    if (usdDueCents > 0L) add(formatMoney(usdDueCents.toInt(), "USD"))
                                }.joinToString(" y ")
                                formError = "Debes cubrir el saldo pendiente ($saldoText)."
                                return@Button
                            }
                        } else if (sum < netTotalCents && !usdCovers && !mixedCurrencies) {
                            formError = "Debes cubrir el total (${formatMoney(netTotalCents.toInt(), sale.currency)})."
                            return@Button
                        }
                        val overpay = (sum - netDueBsCents).coerceAtLeast(0L)
                        val change = changeGivenCents.coerceIn(0L, overpay.coerceAtLeast(changeGivenCents))
                        val method = lines.first().method.toStoreSaleMethod()
                        val ref = lines
                            .filter { it.method.requiresProofOrReference() }
                            .map { normalizePaymentReference(it.method, it.notes) }
                            .filter { it.isNotBlank() }
                            .distinct()
                            .joinToString(" · ")
                        val mixedNotes = buildString {
                            val base = ref.ifBlank { sale.notes.orEmpty().trim() }
                            if (base.isNotBlank()) append(base)
                            if (lines.size > 1) {
                                if (isNotEmpty()) append(" · ")
                                append("Cobro: ${paymentLinesSummaryLabel(lines, dueInput)}")
                            }
                            if (paidUsd > 0L) {
                                if (isNotEmpty()) append(" · ")
                                append("USD ${formatMoney(paidUsd.toInt(), "USD")}")
                            }
                        }.trim().takeIf { it.isNotBlank() }
                        val proof = lines.firstOrNull { it.proof != null }?.proof
                        val amountPaid = when {
                            sum > 0L -> sum
                            usdCovers -> netTotalCents
                            else -> 1L
                        }
                        // Cobros nuevos como líneas (VES o USD). El backend reemplaza las líneas al guardar:
                        // por eso se envían también los cobros anteriores de la venta.
                        val newPayments = lines.mapNotNull { line ->
                            val lineMethod = line.method.toStoreSaleMethod()
                            if (line.method.usesUsdAmount()) {
                                (foreignCurrencyLineUsdCents(line, dueInput, exchangeQuote) ?: 0L)
                                    .takeIf { it > 0L }
                                    ?.let { AdminStorePaymentLine(lineMethod, "USD", it) }
                            } else {
                                foreignCurrencyLineAmountCents(line, dueInput, exchangeQuote)
                                    .takeIf { it > 0L }
                                    ?.let { AdminStorePaymentLine(lineMethod, "VES", it) }
                            }
                        }
                        formError = null
                        onCollect(
                            AdminStoreSaleUpsert(
                                clientId = sale.clientId,
                                walkInName = if (sale.clientId == null) sale.customerName else null,
                                branchId = sale.branchId,
                                method = method,
                                origin = sale.origin,
                                status = AdminStoreSaleStatus.PAID,
                                saleDate = fechaPago.ifBlank { sale.saleDate },
                                currency = sale.currency,
                                notes = mixedNotes,
                                payments = sale.payments + newPayments,
                                amountPaidCents = amountPaid,
                                changeGivenCents = change,
                                foreignUsdCents = when {
                                    paidUsd > 0L -> paidUsd
                                    method.usesUsdAmount() && dueUsdForCobro > 0L -> dueUsdForCobro
                                    else -> null
                                },
                                discount = discount,
                                items = sale.items.map {
                                    AdminStoreSaleItemUpsert(
                                        productId = it.productId,
                                        productName = it.productName,
                                        qty = it.qty,
                                        unitPriceCents = it.unitPriceCents,
                                    )
                                },
                            ),
                            proof,
                        )
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(containerColor = AdminColors.Primary),
                ) {
                    if (saving) {
                        CircularProgressIndicator(Modifier.size(18.dp), color = Color.White, strokeWidth = 2.dp)
                    } else {
                        Icon(Icons.Outlined.Payments, null, Modifier.size(18.dp), tint = Color.White)
                        Spacer(Modifier.width(8.dp))
                        Text("Cobrar", fontWeight = FontWeight.Bold)
                    }
                }
            }
        }
    }
}

@Composable
private fun StoreSaleDetailDialog(
    sale: AdminStoreSale,
    onDismiss: () -> Unit,
    onMarkPaid: (() -> Unit)? = null,
    onCancel: (() -> Unit)? = null,
    saving: Boolean = false,
) {
    Dialog(onDismissRequest = onDismiss) {
        Column(
            Modifier
                .widthIn(max = 460.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Text("Detalle de la venta", fontWeight = FontWeight.Bold, fontSize = 18.sp)
                IconButton(onClick = onDismiss) {
                    Icon(Icons.Outlined.Close, "Cerrar", tint = AdminColors.TextMuted)
                }
            }
            Spacer(Modifier.height(12.dp))
            CamposDetalle(
                buildList {
                    add("Venta" to "#${sale.saleNumber}")
                    add("Cliente" to sale.customerName)
                    add("Fecha" to sale.saleDate)
                    add("Estado" to sale.statusLabel)
                    add("Método" to sale.methodLabel)
                    add("Origen" to sale.originLabel)
                },
            )
            // Artículos: una línea por producto, a todo el ancho.
            Text("Artículos", color = AdminColors.TextMuted, fontSize = 12.sp, modifier = Modifier.padding(bottom = 4.dp))
            if (sale.items.isEmpty()) {
                DatoDetalle("Resumen", sale.itemsSummary)
            } else {
                sale.items.forEach { item ->
                    Row(Modifier.fillMaxWidth().padding(bottom = 4.dp)) {
                        Text(
                            "${item.qty} × ${item.productName}",
                            fontSize = 14.sp,
                            fontWeight = FontWeight.Medium,
                            modifier = Modifier.weight(1f),
                        )
                        Text(
                            formatMoney(item.lineTotalCents.toInt(), GymlyCurrency.CODE),
                            fontSize = 14.sp,
                            fontWeight = FontWeight.Medium,
                        )
                    }
                }
            }
            Spacer(Modifier.height(8.dp))
            // Montos: solo los conceptos que aplican a esta venta.
            CamposDetalle(
                buildList {
                    add("Total" to sale.displayTotalLabel())
                    sale.payments.forEach { line ->
                        add("Cobro ${line.method.labelEs()}" to formatMoney(line.amountCents.toInt(), line.currency))
                    }
                    // Divisas: lo que entregó el cliente en $ y el cambio en $ (lo que sobra después de los $ cobrados).
                    sale.foreignUsdCents?.takeIf { it > 0L && sale.method == AdminStoreSaleMethod.DIVISAS }?.let { entregado ->
                        add("Entregado (\$)" to formatMoney(entregado.toInt(), "USD"))
                        val cobradoUsd = sale.payments.filter { it.currency == "USD" }.sumOf { it.amountCents }
                        if (entregado > cobradoUsd) {
                            add("Cambio (\$)" to formatMoney((entregado - cobradoUsd).toInt(), "USD"))
                        }
                    }
                    // Un solo descuento: el escrito en $ si existe; si no, el escrito en Bs.
                    if (sale.discountUsdCents <= 0L && sale.discountCents > 0L) {
                        add(
                            "Descuento (Bs)" to (
                                formatMoney(sale.discountCents.toInt(), GymlyCurrency.CODE) +
                                    (sale.discountReason?.let { code ->
                                        " · ${BULK_DISCOUNT_REASONS.firstOrNull { it.first == code }?.second ?: code}"
                                    } ?: "") +
                                    (sale.discountNote?.takeIf { it.isNotBlank() }?.let { " · $it" } ?: "")
                                ),
                        )
                    }
                    if (sale.discountUsdCents > 0L) {
                        add("Descuento (\$)" to formatMoney(sale.discountUsdCents.toInt(), "USD"))
                    }
                    if (sale.changeGivenCents > 0L) {
                        add("Vuelto (Bs)" to formatMoney(sale.changeGivenCents.toInt(), GymlyCurrency.CODE))
                    }
                    if (sale.pendingBsCents > 0L) {
                        add("Pendiente (Bs)" to formatMoney(sale.pendingBsCents.toInt(), GymlyCurrency.CODE))
                    }
                    if (sale.pendingUsdCents > 0L) {
                        add("Pendiente (\$)" to formatMoney(sale.pendingUsdCents.toInt(), "USD"))
                    }
                    // La referencia solo aplica al pago móvil.
                    if (sale.method == AdminStoreSaleMethod.MOBILE_PAYMENT) {
                        sale.notes?.takeIf { it.isNotBlank() }?.let { add("Referencia" to it) }
                    }
                },
            )
            sale.proofUrl?.takeIf { it.isNotBlank() }?.let { url ->
                Spacer(Modifier.height(10.dp))
                ProofAttachmentView(
                    proofUrl = url,
                    proofContentType = sale.proofContentType,
                    accent = AdminColors.Primary,
                    muted = AdminColors.TextMuted,
                    surface = AdminColors.Surface,
                    text = AdminColors.Text,
                    outline = AdminColors.Outline,
                )
            }
            Spacer(Modifier.height(16.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End, verticalAlignment = Alignment.CenterVertically) {
                if (onCancel != null) {
                    OutlinedButton(onClick = onCancel, enabled = !saving) {
                        Text("Cancelar venta", color = AdminColors.Error)
                    }
                    Spacer(Modifier.width(8.dp))
                }
                if (onMarkPaid != null) {
                    Button(
                        onClick = onMarkPaid,
                        enabled = !saving,
                        colors = ButtonDefaults.buttonColors(containerColor = AdminColors.Primary),
                    ) { Text("Cobrar") }
                    Spacer(Modifier.width(8.dp))
                }
                OutlinedButton(onClick = onDismiss, enabled = !saving) { Text("Cerrar") }
            }
        }
    }
}

@Composable
private fun DetailLine(label: String, value: String) {
    Row(Modifier.fillMaxWidth().padding(vertical = 4.dp)) {
        Text(label, Modifier.width(110.dp), color = AdminColors.TextMuted, fontSize = 13.sp)
        Text(value, color = AdminColors.Text, fontSize = 13.sp, fontWeight = FontWeight.Medium)
    }
}

@Composable
private fun StoreSaleFormDialog(
    title: String,
    initial: AdminStoreSale?,
    products: List<AdminProduct>,
    companyId: String?,
    clients: List<AdminClient>,
    workers: List<AdminWorker>,
    saving: Boolean,
    /** Error del servidor al guardar (p. ej. stock insuficiente): se muestra dentro del diálogo. */
    serverError: String? = null,
    onDismiss: () -> Unit,
    onSave: (AdminStoreSaleUpsert, PickedImagePayload?) -> Unit,
) {
    var clientId by remember {
        mutableStateOf(initial?.clientId)
    }
    /** true si el comprador elegido es un trabajador (se guarda como walk-in etiquetado). */
    var buyerIsWorker by remember {
        mutableStateOf(initial?.clientId == null && AdminStoreWalkInCustomer.isWorkerLabel(initial?.customerName))
    }
    var clientQuery by remember {
        mutableStateOf(
            initial?.customerName
                ?.takeIf { it.isNotBlank() && !AdminStoreWalkInCustomer.isDefaultLabel(it) }
                ?.let { AdminStoreWalkInCustomer.stripWorkerLabel(it) }
                .orEmpty(),
        )
    }
    val branchId = companyId
    // Pagos ya registrados de la venta, en la moneda en que se cobraron (USD como $, Bs como Bs).
    val savedPaymentLines = remember(initial) {
        initial?.payments.orEmpty().map { line ->
            PaymentLineDraft(
                method = line.method.toPaymentMethod(),
                amountInput = centsToAmountInput(line.amountCents),
            )
        }
    }
    var paymentLines by remember(initial) { mutableStateOf(savedPaymentLines) }
    var fechaPago by remember(initial) { mutableStateOf(fechaPagoVenta(initial?.saleDate.orEmpty(), todayIsoDate())) }
    var changeGivenCents by remember { mutableStateOf(initial?.changeGivenCents ?: 0L) }
    var pendingBsInput by remember {
        mutableStateOf(initial?.pendingBsCents?.takeIf { it > 0L }?.let { centsToAmountInput(it) } ?: AMOUNT_INPUT_ZERO)
    }
    var pendingUsdInput by remember {
        mutableStateOf(initial?.pendingUsdCents?.takeIf { it > 0L }?.let { centsToAmountInput(it) } ?: AMOUNT_INPUT_ZERO)
    }
    var walletOn by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) {
        walletOn = runCatching { AdminRepo.api.getSettingsModules().clientAccountsEnabled }.getOrDefault(false)
    }
    // Saldo de una venta pendiente: lo que falta al abrirla. Es el monto inicial del abono (editable).
    val saldoBsCents = initial?.pendingBsCents ?: 0L
    val saldoUsdCents = initial?.pendingUsdCents ?: 0L
    val isAbono = saldoBsCents > 0L || saldoUsdCents > 0L
    val saldoBsInput = saldoBsCents.takeIf { it > 0L }?.let { centsToAmountInput(it) }.orEmpty()
    val saldoUsdInput = saldoUsdCents.takeIf { it > 0L }?.let { centsToAmountInput(it) }.orEmpty()
    var creditAppliedCents by remember { mutableStateOf(0L) }
    var notes by remember { mutableStateOf(initial?.notes.orEmpty()) }
    var proofPayload by remember { mutableStateOf<PickedImagePayload?>(null) }
    val proofHolder = remember { PickedMediaHolder() }
    var lines by remember {
        mutableStateOf(
            initial?.items?.map {
                DraftLine(it.productId, it.productName, it.qty, it.unitPriceCents)
            } ?: emptyList(),
        )
    }
    var formError by remember { mutableStateOf<String?>(null) }
    var showPersonMatches by remember { mutableStateOf(false) }
    // "Pagar más tarde" solo si la venta está pendiente y sin cobros: con cobros es un abono.
    var payLater by remember(initial) {
        mutableStateOf(initial?.status == AdminStoreSaleStatus.PENDING && initial?.payments.isNullOrEmpty())
    }
    var exchangeQuote by remember { mutableStateOf<org.gymly.model.AdminExchangeRateQuote?>(null) }
    var exchangeRatesOn by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        exchangeRatesOn = runCatching { AdminRepo.api.getSettingsModules().exchangeRatesEnabled }.getOrDefault(false)
        // Con el módulo de tasas apagado la cotización no se usa: ni hint ni conversiones.
        exchangeQuote = if (exchangeRatesOn) runCatching { AdminRepo.api.currentExchangeRate() }.getOrNull() else null
    }

    val total = lines.sumOf { it.lineTotalCents }
    // Precio en $ del catálogo por línea y total (base de cobro cuando se paga en divisas).
    fun lineUsdCents(line: DraftLine): Long =
        (products.firstOrNull { it.id == line.productId }?.priceUsdCents ?: 0L).coerceAtLeast(0L) * line.qty.coerceAtLeast(0)
    val totalUsdCents = lines.sumOf { lineUsdCents(it) }
    val amountDueCents = (total - creditAppliedCents).coerceAtLeast(0L)
    val amountDueInput = centsToAmountInput(amountDueCents)

    // Abono: el pendiente = saldo menos cobro. Si se digita a mano, deja de recalcularse.
    // Al editar, el pendiente guardado es manual: no se recalcula solo. Para recalcular está el botón.
    var pendingEditedByHand by remember(initial) { mutableStateOf(initial != null) }
    var discountBsInput by remember { mutableStateOf("") }
    var discountUsdInput by remember { mutableStateOf("") }
    var discountReason by remember { mutableStateOf("") }
    var discountNote by remember { mutableStateOf("") }
    val abonoLines = paymentLines.ifEmpty { listOf(PaymentLineDraft(amountInput = saldoBsInput)) }
    // Al editar sin cambiar las líneas, los cobros ya están descontados en el saldo: el pendiente es el saldo.
    val unchangedSavedLines = initial != null && paymentLines == savedPaymentLines
    val remainingBsCents = if (unchangedSavedLines) saldoBsCents else
        (saldoBsCents - paymentLinesBsCents(abonoLines, saldoBsInput, exchangeQuote)).coerceAtLeast(0L)
    val remainingUsdCents = if (unchangedSavedLines) saldoUsdCents else
        (saldoUsdCents - paymentLinesUsdCents(abonoLines, saldoUsdInput, exchangeQuote)).coerceAtLeast(0L)
    LaunchedEffect(remainingBsCents, remainingUsdCents, isAbono, payLater) {
        if (payLater) {
            // Pagar más tarde: queda pendiente el total; las casillas muestran el total y no se editan.
            pendingEditedByHand = false
            pendingBsInput = remainingBsCents.takeIf { it > 0L }?.let { centsToAmountInput(it) } ?: AMOUNT_INPUT_ZERO
            pendingUsdInput = remainingUsdCents.takeIf { it > 0L }?.let { centsToAmountInput(it) } ?: AMOUNT_INPUT_ZERO
            return@LaunchedEffect
        }
        if (!isAbono || pendingEditedByHand) return@LaunchedEffect
        pendingBsInput = remainingBsCents.takeIf { it > 0L }?.let { centsToAmountInput(it) } ?: AMOUNT_INPUT_ZERO
        pendingUsdInput = remainingUsdCents.takeIf { it > 0L }?.let { centsToAmountInput(it) } ?: AMOUNT_INPUT_ZERO
    }
    val personMatches = remember(clientQuery, clients, workers) {
        storeSalePersonOptions(clients, workers, clientQuery)
    }

    val resolvedPaymentLines = paymentLines.ifEmpty {
        listOf(
            PaymentLineDraft(
                method = org.gymly.model.AdminPaymentMethod.CASH,
                amountInput = amountDueInput.ifBlank { centsToAmountInput(total) },
            ),
        )
    }

    // Si hay líneas de cobro con monto, no puede quedar «pagar más tarde».
    LaunchedEffect(paymentLines, payLater) {
        if (!payLater) return@LaunchedEffect
        val hasPayment = paymentLines.any {
            it.amountInput.isNotBlank() || it.vesAmountInput.isNotBlank() || it.notes.isNotBlank()
        }
        // Al editar una venta pendiente, las líneas vienen de lo ya guardado: no es un cobro nuevo.
        if (hasPayment && paymentLines != savedPaymentLines) payLater = false
    }

    // Mostrador: Pagada salvo que se tilde "Pagar más tarde".
    val effectiveStatus = when {
        initial?.status == AdminStoreSaleStatus.FAILED -> AdminStoreSaleStatus.FAILED
        payLater -> AdminStoreSaleStatus.PENDING
        else -> AdminStoreSaleStatus.PAID
    }

    fun resolveWalkInNameForSave(): String? {
        if (clientId != null) return null
        val typed = clientQuery.trim()
        if (buyerIsWorker && typed.isNotBlank()) {
            return AdminStoreWalkInCustomer.workerLabel(typed)
        }
        // Si el texto coincide exactamente con un trabajador activo, etiquetarlo.
        val matchedWorker = workers.firstOrNull {
            it.active && it.displayName.equals(typed, ignoreCase = true)
        }
        if (matchedWorker != null) {
            return AdminStoreWalkInCustomer.workerLabel(matchedWorker.displayName)
        }
        return AdminStoreWalkInCustomer.resolveLabel(typed)
    }

    Dialog(
        onDismissRequest = { if (!saving) onDismiss() },
        properties = DialogProperties(dismissOnClickOutside = false, usePlatformDefaultWidth = false),
    ) {
        Column(
            Modifier
                // Una sola columna: encabezado y pie fijos; el contenido del medio se desplaza.
                .widthIn(max = 640.dp)
                .fillMaxWidth(0.92f)
                .fillMaxHeight(0.92f)
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Text(title, fontWeight = FontWeight.Bold, fontSize = 18.sp)
            Spacer(Modifier.height(14.dp))
            Column(Modifier.weight(1f).fillMaxWidth().verticalScroll(rememberScrollState())) {
            // Orden de arriba abajo: cliente, artículos, cobro y confirmación (una sola columna).
            BoxWithConstraints(Modifier.fillMaxWidth()) {
            val narrow = maxWidth < 600.dp
            val leftColumn: @Composable () -> Unit = {
            Column(Modifier.fillMaxWidth()) {
            Text("Cliente / trabajador", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            Box {
                OutlinedTextField(
                    value = clientQuery,
                    onValueChange = { value ->
                        clientQuery = value
                        val trimmed = value.trim()
                        val matchedClient = clients.firstOrNull {
                            it.fullName.equals(trimmed, ignoreCase = true)
                        }
                        val matchedWorker = workers.firstOrNull {
                            it.active && it.displayName.equals(trimmed, ignoreCase = true)
                        }
                        clientId = matchedClient?.id
                        buyerIsWorker = matchedClient == null && matchedWorker != null
                        showPersonMatches = trimmed.length >= 2
                    },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    placeholder = {
                        Text(AdminStoreWalkInCustomer.EXTERNAL_CLIENT.labelEs, color = AdminColors.TextMuted)
                    },
                    shape = RoundedCornerShape(10.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = AdminColors.Primary,
                        unfocusedBorderColor = AdminColors.Outline,
                    ),
                )
                DropdownMenu(
                    expanded = showPersonMatches && personMatches.isNotEmpty() && clientId == null,
                    onDismissRequest = { showPersonMatches = false },
                ) {
                    personMatches.forEach { option ->
                        DropdownMenuItem(
                            text = { Text(option.listLabel) },
                            onClick = {
                                clientQuery = option.name
                                clientId = option.clientId
                                buyerIsWorker = option.clientId == null
                                showPersonMatches = false
                            },
                        )
                    }
                }
            }
            if (buyerIsWorker && clientQuery.isNotBlank()) {
                Text(
                    "Se registrará como trabajador: ${AdminStoreWalkInCustomer.workerLabel(clientQuery)}",
                    color = AdminColors.Primary,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Medium,
                    modifier = Modifier.padding(top = 4.dp),
                )
            } else {
                Text(
                    "Busca socio o trabajador. Si no eliges, se registra como ${AdminStoreWalkInCustomer.EXTERNAL_CLIENT.labelEs}.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(top = 4.dp),
                )
            }

            Spacer(Modifier.height(14.dp))
            Text("Artículos", fontWeight = FontWeight.SemiBold)
            Spacer(Modifier.height(8.dp))
            // Artículos: hasta cinco filas visibles; si hay más, la lista se desplaza dentro de su bloque.
            Column(Modifier.fillMaxWidth().heightIn(max = 300.dp).verticalScroll(rememberScrollState())) {
            lines.forEachIndexed { index, line ->
                var productMenu by remember(index) { mutableStateOf(false) }
                val productQuery = line.search.trim()
                val productMatches = if (productQuery.length < 1 || productQuery.equals(line.productName, ignoreCase = true)) {
                    emptyList()
                } else {
                    products.filter { product ->
                        product.name.contains(productQuery, ignoreCase = true)
                    }.take(8)
                }
                val productSearch: @Composable (Modifier) -> Unit = { mod ->
                    Box(mod) {
                        OutlinedTextField(
                            value = line.search,
                            onValueChange = { value ->
                                val keepSelection = value.trim().equals(line.productName, ignoreCase = true) &&
                                    !line.productId.isNullOrBlank()
                                lines = lines.toMutableList().also {
                                    it[index] = line.copy(
                                        search = value,
                                        productId = if (keepSelection) line.productId else null,
                                        productName = if (keepSelection) line.productName else "",
                                        unitPriceCents = if (keepSelection) line.unitPriceCents else 0,
                                    )
                                }
                                // Sugerencias solo al escribir; al borrar se cierran para no confundirlas con autocompletar.
                                productMenu = value.trim().isNotEmpty() && value.length > line.search.length
                            },
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true,
                            placeholder = { Text("Nombre del producto", color = AdminColors.TextMuted) },
                            shape = RoundedCornerShape(10.dp),
                            colors = OutlinedTextFieldDefaults.colors(
                                focusedBorderColor = AdminColors.Primary,
                                unfocusedBorderColor = AdminColors.Outline,
                            ),
                        )
                        DropdownMenu(
                            expanded = productMenu && productMatches.isNotEmpty(),
                            onDismissRequest = { productMenu = false },
                        ) {
                            productMatches.forEach { p ->
                                DropdownMenuItem(
                                    text = { Text(productOptionLabel(p), maxLines = 1) },
                                    onClick = {
                                        lines = lines.toMutableList().also {
                                            it[index] = DraftLine(
                                                p.id,
                                                p.name,
                                                line.qty.coerceAtLeast(1),
                                                p.priceCents,
                                            )
                                        }
                                        productMenu = false
                                    },
                                )
                            }
                        }
                    }
                }
                val stepper: @Composable () -> Unit = {
                    QuantityStepper(
                        value = line.qty,
                        onValueChange = { q ->
                            lines = lines.toMutableList().also {
                                it[index] = line.copy(qty = q.coerceAtLeast(0))
                            }
                        },
                        enabled = !saving,
                    )
                }
                val lineTotal: @Composable (Modifier) -> Unit = { mod ->
                    Column(mod) {
                        Text(
                            formatMoney(line.lineTotalCents.toInt(), GymlyCurrency.CODE),
                            fontSize = 13.sp,
                        )
                        val lineUsd = lineUsdCents(line)
                        if (lineUsd > 0L) {
                            Text(
                                formatMoney(lineUsd.toInt(), "USD"),
                                color = AdminColors.TextMuted,
                                fontSize = 11.sp,
                            )
                        }
                    }
                }
                val removeLine: @Composable () -> Unit = {
                    IconButton(
                        onClick = {
                            lines = lines.toMutableList().also { it.removeAt(index) }
                        },
                    ) {
                        Icon(Icons.Outlined.Delete, "Quitar", tint = AdminColors.Error)
                    }
                }
                // El nombre del producto ocupa todo el ancho de la columna; debajo: cantidad, total y quitar.
                if (narrow) {
                    // Celular: nombre a todo el ancho; cantidad, total y quitar debajo.
                    Column(
                        Modifier.fillMaxWidth().padding(bottom = 8.dp),
                        verticalArrangement = Arrangement.spacedBy(6.dp),
                    ) {
                        productSearch(Modifier.fillMaxWidth())
                        Row(
                            Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            stepper()
                            lineTotal(Modifier.weight(1f))
                            removeLine()
                        }
                    }
                } else {
                    // Escritorio: una fila por artículo (nombre, cantidad, total y quitar).
                    Row(
                        Modifier.fillMaxWidth().padding(bottom = 8.dp),
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        productSearch(Modifier.weight(1f))
                        stepper()
                        lineTotal(Modifier.width(96.dp))
                        removeLine()
                    }
                }
                // Stock: se avisa en la fila antes de cobrar, no después.
                val pickedStock = products.firstOrNull { it.id == line.productId }?.stockQty
                if (pickedStock != null && line.qty > pickedStock) {
                    Text(
                        if (pickedStock <= 0) {
                            "Agotado: no hay existencias de ${line.productName}."
                        } else {
                            "Stock insuficiente: quedan $pickedStock de ${line.productName}."
                        },
                        color = AdminColors.Error,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Medium,
                        modifier = Modifier.padding(bottom = 8.dp),
                    )
                }
            }
            }
            TextButton(
                onClick = { lines = lines + DraftLine(null, "", 1, 0) },
            ) { Text("+ Agregar producto", color = AdminColors.Primary) }

            Spacer(Modifier.height(8.dp))
            val showDualTotal = resolvedPaymentLines.any { it.method.usesDualCurrency() }
            if (showDualTotal && exchangeRatesOn) {
                DualCurrencyTotalHint(total, exchangeQuote, Modifier.align(Alignment.End))
            } else {
                Text(
                    "Total: ${formatMoney(total.toInt(), GymlyCurrency.CODE)}",
                    Modifier.align(Alignment.End),
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                )
            }
            if (totalUsdCents > 0L) {
                Text(
                    "Total: ${formatMoney(totalUsdCents.toInt(), "USD")}",
                    Modifier.align(Alignment.End),
                    fontWeight = FontWeight.Bold,
                    fontSize = 16.sp,
                )
            }
            if (creditAppliedCents > 0) {
                Spacer(Modifier.height(4.dp))
                Text(
                    "Saldo aplicado: ${formatMoney(creditAppliedCents.toInt(), GymlyCurrency.CODE)} · " +
                        "Por cobrar: ${formatMoney(amountDueCents.toInt(), GymlyCurrency.CODE)}",
                    Modifier.align(Alignment.End),
                    color = AdminColors.Primary,
                    fontSize = 12.sp,
                )
            }

            }
            }
            val rightColumn: @Composable () -> Unit = {
            Column(Modifier.fillMaxWidth()) {
            // Arriba del cobro: si queda debajo del formulario, en pantallas bajas parece “no existir”.
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(10.dp))
                    .background(AdminColors.Primary.copy(alpha = 0.06f))
                    .clickable(enabled = !saving) { payLater = !payLater }
                    .padding(horizontal = 8.dp, vertical = 6.dp),
            ) {
                Checkbox(
                    checked = payLater,
                    onCheckedChange = { if (!saving) payLater = it },
                    enabled = !saving,
                    colors = CheckboxDefaults.colors(
                        checkedColor = AdminColors.Primary,
                        uncheckedColor = AdminColors.Outline,
                    ),
                )
                Column {
                    Text(
                        "Pagar más tarde",
                        fontWeight = FontWeight.SemiBold,
                        color = AdminColors.Text,
                        fontSize = 13.sp,
                    )
                    Text(
                        "Solo si lo marcas: la venta queda pendiente y se cobra después.",
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                    )
                }
            }
            Spacer(Modifier.height(6.dp))
            Text(
                "Total a cobrar: ${formatMoney(total.toInt(), GymlyCurrency.CODE)}",
                fontWeight = FontWeight.Bold,
                fontSize = 16.sp,
                color = AdminColors.Text,
            )
            if (totalUsdCents > 0L) {
                Text(
                    "Total a cobrar: ${formatMoney(totalUsdCents.toInt(), "USD")}",
                    fontWeight = FontWeight.Bold,
                    fontSize = 16.sp,
                    color = AdminColors.Text,
                )
            }
            Spacer(Modifier.height(6.dp))
            Text(
                "Estado: ${effectiveStatus.labelEs()}",
                color = if (payLater) Color(0xFFE65100) else AdminColors.Primary,
                fontSize = 13.sp,
                fontWeight = FontWeight.SemiBold,
            )
            if (!payLater) {
                Text(
                    "Sin tilde: el cobro debe cubrir el total y la venta queda pagada.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(top = 4.dp),
                )
                Spacer(Modifier.height(12.dp))
                CombinedPaymentLinesForm(
                    totalAmountInput = if (isAbono) saldoBsInput else {
                        // Con descuento, el total de cobertura es el neto (monto menos descuento).
                        val discountLiveCents = discountLiveBsCents(
                            parseBulkAmountToCents(discountBsInput),
                            parseBulkAmountToCents(discountUsdInput),
                            exchangeQuote,
                            exchangeRatesOn,
                        )
                        if (discountLiveCents > 0L) {
                            centsToAmountInput(
                                (parseBulkAmountToCents(amountDueInput.ifBlank { centsToAmountInput(total) }) - discountLiveCents)
                                    .coerceAtLeast(0L),
                            )
                        } else {
                            amountDueInput.ifBlank { centsToAmountInput(total) }
                        }
                    },
                    defaultLineAmount = if (isAbono) saldoBsInput else centsToAmountInput(total),
                    defaultUsdAmount = if (isAbono) saldoUsdInput else {
                        val catalogUsdCents = lines.sumOf { line ->
                            val unitUsd = products.firstOrNull { it.id == line.productId }?.priceUsdCents ?: 0L
                            unitUsd.coerceAtLeast(0L) * line.qty.coerceAtLeast(0)
                        }
                        // Tasa apagada: el descuento en $ escrito a mano baja el monto en $ a cobrar.
                        val manualUsdDiscountCents = if (exchangeRatesOn) 0L else parseBulkAmountToCents(discountUsdInput)
                        (catalogUsdCents - manualUsdDiscountCents).takeIf { it > 0L }?.let { centsToAmountInput(it) }.orEmpty()
                    },
                    enabled = !saving,
                    allowOverpay = true,
                    enforcePlanCoverage = true,
                    checkExistingMobilePayments = true,
                    paymentDateIso = fechaPago,
                    paymentDateEditable = true,
                    onPaymentDateChange = { fechaPago = it },
                    clientId = clientId,
                    initialLines = savedPaymentLines,
                    initialChangeGivenCents = changeGivenCents,
                    onCreditApplied = { applied -> creditAppliedCents += applied },
                    onChangeGivenCentsChange = { changeGivenCents = it },
                    onLinesChange = { paymentLines = it },
                ) { _, _, changeGiven, _ ->
                    changeGivenCents = changeGiven
                }
            }

            if (!isAbono && !payLater) {
                Spacer(Modifier.height(12.dp))
                BulkDiscountSection(
                    bsInput = discountBsInput,
                    onBsInputChange = { discountBsInput = it },
                    usdInput = discountUsdInput,
                    onUsdInputChange = { discountUsdInput = it },
                    reason = discountReason,
                    onReasonChange = { discountReason = it },
                    note = discountNote,
                    onNoteChange = { discountNote = it },
                    enabled = !saving,
                )
            }
            Spacer(Modifier.height(12.dp))
            Text("Queda pendiente (manual)", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                OutlinedTextField(
                    value = pendingBsInput,
                    onValueChange = {
                        pendingBsInput = it
                        pendingEditedByHand = true
                        // Cobro de una sola moneda: si escribes Bs, el pendiente en $ queda en 0,00 (en mixto no se toca).
                        if (parseBulkAmountToCents(it) > 0L && cobroEsDeUnaMoneda(paymentLines)) pendingUsdInput = AMOUNT_INPUT_ZERO
                    },
                    label = { Text("Bs") },
                    singleLine = true,
                    enabled = !saving && !payLater,
                    modifier = Modifier.weight(1f),
                )
                OutlinedTextField(
                    value = pendingUsdInput,
                    onValueChange = {
                        pendingUsdInput = it
                        pendingEditedByHand = true
                        // Cobro de una sola moneda: si escribes $, el pendiente en Bs queda en 0,00 (en mixto no se toca).
                        if (parseBulkAmountToCents(it) > 0L && cobroEsDeUnaMoneda(paymentLines)) pendingBsInput = AMOUNT_INPUT_ZERO
                    },
                    label = { Text("USD") },
                    singleLine = true,
                    enabled = !saving && !payLater,
                    modifier = Modifier.weight(1f),
                )
            }
            Text(
                "Lo que aún falta por cobrar, en Bs y en USD. Vacío o cero = pagada. Con saldo, la venta queda Pendiente y admite abonos.",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
            if (isAbono && pendingEditedByHand) {
                TextButton(
                    onClick = {
                        pendingEditedByHand = false
                        pendingBsInput = remainingBsCents.takeIf { it > 0L }?.let { centsToAmountInput(it) } ?: AMOUNT_INPUT_ZERO
                        pendingUsdInput = remainingUsdCents.takeIf { it > 0L }?.let { centsToAmountInput(it) } ?: AMOUNT_INPUT_ZERO
                    },
                    enabled = !saving,
                ) {
                    Text("Volver a calcular desde el cobro", fontSize = 12.sp)
                }
            }

            // Excedente en $ sobre la deuda en $: pendiente en $ previo o precio en $ de los productos.
            val catalogUsdCents = lines.sumOf { line ->
                val unitUsd = products.firstOrNull { p -> p.id == line.productId }?.priceUsdCents ?: 0L
                unitUsd.coerceAtLeast(0L) * line.qty.coerceAtLeast(0)
            }
            val dueUsdCents = if (saldoUsdCents > 0L) saldoUsdCents else catalogUsdCents
            val formDueInput = amountDueInput.ifBlank { centsToAmountInput(total) }
            val typedUsdCents = paymentLines.sumOf { line ->
                if (line.method.usesUsdAmount()) foreignCurrencyLineUsdCents(line, formDueInput, exchangeQuote) ?: 0L else 0L
            }
            val usdExcessCents = if (dueUsdCents > 0L) (typedUsdCents - dueUsdCents).coerceAtLeast(0L) else 0L
            if (usdExcessCents > 0L) {
                Spacer(Modifier.height(8.dp))
                val excessText = formatMoney(usdExcessCents.toInt(), "USD")
                Text(
                    if (walletOn && !clientId.isNullOrBlank()) {
                        "Excedente $excessText: se acredita al monedero del cliente."
                    } else {
                        "Excedente $excessText: se registra como entregado en USD; el vuelto se declara aparte (en Bs)."
                    },
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
            }
            formError?.let {
                Spacer(Modifier.height(8.dp))
                Text(it, color = AdminColors.Error, fontSize = 13.sp)
            }

            }
            }
            Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                leftColumn()
                rightColumn()
            }
            }
            }
            serverError?.let {
                Spacer(Modifier.height(10.dp))
                Text(it, color = AdminColors.Error, fontSize = 13.sp, fontWeight = FontWeight.Medium)
            }
            Spacer(Modifier.height(18.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                OutlinedButton(onClick = onDismiss, enabled = !saving) { Text("Cancelar") }
                Spacer(Modifier.width(8.dp))
                Button(
                    onClick = {
                        val incomplete = lines.any { it.productId.isNullOrBlank() || it.qty <= 0 }
                        val validLines = lines.filter { !it.productId.isNullOrBlank() && it.qty > 0 }
                        val overStock = validLines.any { l ->
                            products.firstOrNull { it.id == l.productId }?.let { l.qty > it.stockQty } == true
                        }
                        when {
                            incomplete -> formError = "Selecciona un producto en cada fila (o quita las vacías)."
                            overStock -> formError = "Hay artículos sin stock suficiente: ajusta la cantidad o quítalos."
                            validLines.isEmpty() -> formError = "Agrega al menos un artículo."
                            payLater -> {
                                formError = null
                                onSave(
                                    AdminStoreSaleUpsert(
                                        clientId = clientId,
                                        walkInName = resolveWalkInNameForSave(),
                                        branchId = branchId,
                                        saleDate = fechaPago,
                                        method = AdminStoreSaleMethod.CASH,
                                        origin = AdminStoreSaleOrigin.STAFF_REGISTERED,
                                        status = AdminStoreSaleStatus.PENDING,
                                        currency = GymlyCurrency.CODE,
                                        notes = notes.trim().takeIf { it.isNotBlank() },
                                        amountPaidCents = 0L,
                                        changeGivenCents = 0L,
                                        pendingBsCents = parseBulkAmountToCents(pendingBsInput),
                                        pendingUsdCents = parseBulkAmountToCents(pendingUsdInput),
                                        payments = initial?.payments?.takeIf { it.isNotEmpty() },
                                        items = validLines.map {
                                            AdminStoreSaleItemUpsert(
                                                productId = it.productId,
                                                productName = it.productName,
                                                qty = it.qty,
                                                unitPriceCents = it.unitPriceCents,
                                            )
                                        },
                                    ),
                                    null,
                                )
                            }
                            amountDueCents > 0 -> {
                                val dueInput = amountDueInput.ifBlank { centsToAmountInput(total) }
                                val payLines = paymentLines.ifEmpty {
                                    listOf(
                                        PaymentLineDraft(amountInput = dueInput),
                                    )
                                }
                                val sum = paymentLinesBsCents(payLines, dueInput, exchangeQuote)
                                val paidUsd = payLines.sumOf {
                                    if (it.method.usesUsdAmount()) {
                                        foreignCurrencyLineUsdCents(it, dueInput, exchangeQuote) ?: 0L
                                    } else {
                                        0L
                                    }
                                }
                                val catalogUsdCents = validLines.sumOf { line ->
                                    val unitUsd = products.firstOrNull { it.id == line.productId }?.priceUsdCents ?: 0L
                                    unitUsd.coerceAtLeast(0L) * line.qty.coerceAtLeast(0)
                                }
                                // Vacío no es cero: el pendiente se escribe siempre (0,00 si no queda saldo).
                                if (pendingBsInput.isBlank() || pendingUsdInput.isBlank()) {
                                    formError = "Escribe el pendiente en Bs y en $ (0,00 si no queda saldo)."
                                    return@Button
                                }
                                val pendingBs = parseBulkAmountToCents(pendingBsInput)
                                val pendingUsd = parseBulkAmountToCents(pendingUsdInput)
                                val isPaid = pendingBs == 0L && pendingUsd == 0L
                                val (discount, discountError) = bulkDiscountOrError(
                                    bsInput = if (isAbono) "" else discountBsInput,
                                    reason = discountReason,
                                    note = discountNote,
                                    hasPending = !isPaid,
                                    hasWalletCredit = creditAppliedCents > 0L,
                                    hasLines = payLines.isNotEmpty(),
                                    usdInput = if (isAbono) "" else discountUsdInput,
                                )
                                if (discountError != null) {
                                    formError = discountError
                                    return@Button
                                }
                                // Con descuento, lo que se cobra en Bs es el monto menos el descuento.
                                val netDueCents = (amountDueCents - (discount?.cents ?: 0L)).coerceAtLeast(0L)
                                // Tasa apagada: un descuento escrito a mano (en Bs o en $) cierra la venta; el operador lo declara.
                                val descuentoManual = !exchangeRatesOn &&
                                    ((discount?.cents ?: 0L) > 0L || (discount?.usdCents ?: 0L) > 0L)
                                val planUsdForValidation = when {
                                    catalogUsdCents > 0L -> catalogUsdCents
                                    paidUsd > 0L && sum < netDueCents -> paidUsd
                                    else -> 0L
                                }
                                // En un abono el saldo lo define el pendiente digitado, no el total.
                                val priorPayments = initial?.payments.orEmpty()
                                if (isPaid && !isAbono && !descuentoManual) paymentSumValidationError(
                                    sumCents = sum,
                                    targetCents = netDueCents,
                                    allowOverpay = true,
                                    enforcePlanCoverage = true,
                                    paidUsdCents = paidUsd,
                                    planUsdCents = planUsdForValidation,
                                )?.let {
                                    formError = it
                                    return@Button
                                }
                                val usdCovers = paidUsd > 0L &&
                                    (catalogUsdCents <= 0L || paidUsd >= catalogUsdCents)
                                if (isPaid && !isAbono && !descuentoManual && sum < netDueCents && !usdCovers && !isMixedCurrencyPayment(sum, paidUsd)) {
                                    formError = "Debes cubrir el total (${formatMoney(netDueCents.toInt(), GymlyCurrency.CODE)}). La venta queda pagada."
                                } else {
                                    val overpay = (sum - netDueCents).coerceAtLeast(0L)
                                    val change = changeGivenCents.coerceIn(0L, overpay.coerceAtLeast(changeGivenCents))
                                    val saleMethod = payLines.first().method.toStoreSaleMethod()
                                    val refFromLines = payLines
                                        .filter { it.method.requiresProofOrReference() }
                                        .map { normalizePaymentReference(it.method, it.notes) }
                                        .filter { it.isNotBlank() }
                                        .distinct()
                                        .joinToString(" · ")
                                    val mixedNotes = buildString {
                                        val base = refFromLines.ifBlank { notes.trim() }
                                        if (base.isNotBlank()) append(base)
                                        if (payLines.size > 1) {
                                            if (isNotEmpty()) append(" · ")
                                            append("Cobro: ${paymentLinesSummaryLabel(payLines, dueInput)}")
                                        }
                                        if (paidUsd > 0L) {
                                            if (isNotEmpty()) append(" · ")
                                            append("USD ${formatMoney(paidUsd.toInt(), "USD")}")
                                        }
                                    }.trim().takeIf { it.isNotBlank() }
                                    val lineProof = payLines.firstOrNull { it.proof != null }?.proof
                                    val amountPaid = when {
                                        !isPaid -> sum
                                        sum > 0L -> sum.coerceAtLeast(netDueCents)
                                        usdCovers -> netDueCents
                                        else -> netDueCents.coerceAtLeast(1L)
                                    }
                                    formError = null
                                    // Cobrar ahora: siempre Pagada (pago móvil / divisas incluido).
                                    onSave(
                                        AdminStoreSaleUpsert(
                                            clientId = clientId,
                                            walkInName = resolveWalkInNameForSave(),
                                            branchId = branchId,
                                            saleDate = fechaPago,
                                            method = saleMethod,
                                            origin = AdminStoreSaleOrigin.STAFF_REGISTERED,
                                            status = if (isPaid) AdminStoreSaleStatus.PAID else AdminStoreSaleStatus.PENDING,
                                            currency = GymlyCurrency.CODE,
                                            notes = mixedNotes,
                                            // Venta normal: las líneas ya traen todos los cobros (al editar se cargan desde el guardado).
                                            // Solo un abono conserva los cobros anteriores y agrega el nuevo.
                                            payments = (if (isAbono) priorPayments else emptyList()) + payLines.mapNotNull { line ->
                                                val method = line.method.toStoreSaleMethod()
                                                if (line.method.usesUsdAmount()) {
                                                    (foreignCurrencyLineUsdCents(line, dueInput, exchangeQuote) ?: 0L)
                                                        .takeIf { it > 0L }
                                                        ?.let { AdminStorePaymentLine(method, "USD", it) }
                                                } else {
                                                    foreignCurrencyLineAmountCents(line, dueInput, exchangeQuote)
                                                        .takeIf { it > 0L }
                                                        ?.let { AdminStorePaymentLine(method, "VES", it) }
                                                }
                                            },
                                            pendingBsCents = pendingBs,
                                            pendingUsdCents = pendingUsd,
                                            amountPaidCents = amountPaid,
                                            changeGivenCents = change,
                                            foreignUsdCents = when {
                                                paidUsd > 0L -> paidUsd
                                                saleMethod.usesUsdAmount() && catalogUsdCents > 0L -> catalogUsdCents
                                                else -> null
                                            },
                                            discount = discount,
                                            items = validLines.map {
                                                AdminStoreSaleItemUpsert(
                                                    productId = it.productId,
                                                    productName = it.productName,
                                                    qty = it.qty,
                                                    unitPriceCents = it.unitPriceCents,
                                                )
                                            },
                                        ),
                                        lineProof ?: proofHolder.payload ?: proofPayload,
                                    )
                                }
                            }
                            else -> {
                                val lineRef = resolvedPaymentLines
                                    .filter { it.method.requiresProofOrReference() }
                                    .map { normalizePaymentReference(it.method, it.notes) }
                                    .filter { it.isNotBlank() }
                                    .distinct()
                                    .joinToString(" · ")
                                    .ifBlank { notes.trim() }
                                formError = null
                                onSave(
                                    AdminStoreSaleUpsert(
                                        clientId = clientId,
                                        walkInName = resolveWalkInNameForSave(),
                                        branchId = branchId,
                                        saleDate = fechaPago,
                                        method = resolvedPaymentLines.first().method.toStoreSaleMethod(),
                                        origin = AdminStoreSaleOrigin.STAFF_REGISTERED,
                                        status = AdminStoreSaleStatus.PAID,
                                        currency = GymlyCurrency.CODE,
                                        notes = lineRef.takeIf { it.isNotBlank() },
                                        amountPaidCents = total.coerceAtLeast(1L),
                                        items = validLines.map {
                                            AdminStoreSaleItemUpsert(
                                                productId = it.productId,
                                                productName = it.productName,
                                                qty = it.qty,
                                                unitPriceCents = it.unitPriceCents,
                                            )
                                        },
                                    ),
                                    proofHolder.payload ?: proofPayload,
                                )
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(containerColor = AdminColors.Primary),
                ) {
                    if (saving) CircularProgressIndicator(Modifier.size(18.dp), color = Color.White, strokeWidth = 2.dp)
                    else {
                        Icon(
                            Icons.Outlined.Payments,
                            contentDescription = null,
                            modifier = Modifier.size(18.dp),
                            tint = Color.White,
                        )
                        Spacer(Modifier.width(8.dp))
                        Text(if (payLater) "Registrar pendiente" else "Cobrar", fontWeight = FontWeight.Bold)
                    }
                }
            }
        }
    }
}

private fun inventoryAlertNames(
    lines: List<AdminStoreSaleItemUpsert>,
    products: List<AdminProduct>,
    previousItems: List<org.gymly.model.AdminStoreSaleItem> = emptyList(),
): List<String> {
    val sold = mutableMapOf<String, Int>()
    lines.forEach { line ->
        val id = line.productId ?: return@forEach
        sold[id] = (sold[id] ?: 0) + line.qty
    }
    val previous = mutableMapOf<String, Int>()
    previousItems.forEach { item ->
        val id = item.productId ?: return@forEach
        previous[id] = (previous[id] ?: 0) + item.qty
    }
    return sold.mapNotNull { (id, qty) ->
        val product = products.find { it.id == id } ?: return@mapNotNull null
        val minimum = product.minStockQty
        if (minimum <= 0) return@mapNotNull null
        val remaining = product.stockQty + (previous[id] ?: 0) - qty
        if (remaining <= minimum) product.name else null
    }.distinct()
}

private fun inventoryAlertMessage(names: List<String>): String? = when (names.size) {
    0 -> null
    1 -> "Ya no queda inventario de ${names.first()}."
    else -> "Ya no queda inventario de: ${names.joinToString(", ")}."
}

@Composable
private fun QuantityStepper(
    value: Int,
    onValueChange: (Int) -> Unit,
    enabled: Boolean = true,
    modifier: Modifier = Modifier,
) {
    Column(modifier) {
        Text("Cant.", color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(4.dp))
        Row(
            Modifier
                .clip(RoundedCornerShape(10.dp))
                .border(1.dp, AdminColors.Outline, RoundedCornerShape(10.dp)),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            IconButton(
                onClick = { onValueChange((value - 1).coerceAtLeast(0)) },
                enabled = enabled && value > 0,
            ) {
                Icon(Icons.Outlined.Remove, "Disminuir", modifier = Modifier.size(18.dp), tint = AdminColors.Text)
            }
            Text(
                value.toString(),
                modifier = Modifier.widthIn(min = 28.dp).padding(horizontal = 2.dp),
                fontWeight = FontWeight.SemiBold,
                fontSize = 14.sp,
                textAlign = TextAlign.Center,
            )
            IconButton(
                onClick = { onValueChange(value + 1) },
                enabled = enabled,
            ) {
                Icon(Icons.Outlined.Add, "Aumentar", modifier = Modifier.size(18.dp), tint = AdminColors.Text)
            }
        }
    }
}
